/**
 * 메일 시스템 메인 페이지 — 실제 메일 서비스 스타일.
 *
 * 레이아웃:
 *   ┌────────────┬────────────────────────────────────────────────────────┐
 *   │ [메일쓰기]  │ 받은 메일함 11/13,497         [검색] [필터] [새로고침] │
 *   │ [내게쓰기]  ├────────────────────────────────────────────────────────┤
 *   │ ─────────  │  설명: 외부에서 받은 메일이 도착하는 기본 폴더입니다.    │
 *   │ 📥 받은    ├────────────────────────────────────────────────────────┤
 *   │ ✉️ 보낸    │ ☐ [삭제][스팸][답장][전달][이동▾][⋮]   [날짜▾][100▾][≡] │
 *   │ 📝 임시    ├────────────────────────────────────────────────────────┤
 *   │ ⚠️ 스팸    │ ☐ ★ ✉ 보낸이      제목                  날짜      용량 │
 *   │ 🗑️ 휴지통  │ ☐ ★ ✉ Adobe       검토할 ...           05/03    37 KB │
 *   │ 📦 보관함  │ ...                                                     │
 *   │ ─────────  ├────────────────────────────────────────────────────────┤
 *   │ 👤 연락처  │   ◀ 1 2 3 4 5 ... 132 ▶                                  │
 *   └────────────┴────────────────────────────────────────────────────────┘
 *
 * 메일 클릭 → 우측 풀스크린 모달로 상세.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, IconButton, Menu, MenuItem, Stack, Tab, Tabs, TextField, Tooltip, Typography,
} from "@mui/material";
import InboxRoundedIcon            from "@mui/icons-material/InboxRounded";
import AllInboxRoundedIcon         from "@mui/icons-material/AllInboxRounded";
import FolderRoundedIcon           from "@mui/icons-material/FolderRounded";
import CreateNewFolderRoundedIcon  from "@mui/icons-material/CreateNewFolderRounded";
import MoreVertRoundedIcon         from "@mui/icons-material/MoreVertRounded";
import ScheduleSendRoundedIcon     from "@mui/icons-material/ScheduleSendRounded";
import CampaignRoundedIcon         from "@mui/icons-material/CampaignRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import ExpandLessRoundedIcon        from "@mui/icons-material/ExpandLessRounded";
import DrawRoundedIcon             from "@mui/icons-material/DrawRounded";
import ActionButton from "../../component/common/ActionButton";
import CollapsibleSidebar from "../../component/common/CollapsibleSidebar";
import SendRoundedIcon             from "@mui/icons-material/SendRounded";
import DraftsRoundedIcon           from "@mui/icons-material/DraftsRounded";
import ReportRoundedIcon           from "@mui/icons-material/ReportRounded";
import DeleteOutlineRoundedIcon    from "@mui/icons-material/DeleteOutlineRounded";
import ArchiveRoundedIcon          from "@mui/icons-material/ArchiveRounded";
import EditRoundedIcon             from "@mui/icons-material/EditRounded";
import StarRoundedIcon             from "@mui/icons-material/StarRounded";
import StarBorderRoundedIcon       from "@mui/icons-material/StarBorderRounded";
import RefreshRoundedIcon          from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon           from "@mui/icons-material/SearchRounded";
import AttachFileRoundedIcon       from "@mui/icons-material/AttachFileRounded";
import EmailRoundedIcon            from "@mui/icons-material/EmailRounded";
import MarkEmailReadRoundedIcon    from "@mui/icons-material/MarkEmailReadRounded";
import MarkEmailUnreadRoundedIcon  from "@mui/icons-material/MarkEmailUnreadRounded";
import ContactsRoundedIcon         from "@mui/icons-material/ContactsRounded";
import GroupsRoundedIcon           from "@mui/icons-material/GroupsRounded";
import GroupAddRoundedIcon         from "@mui/icons-material/GroupAddRounded";
import BlockRoundedIcon             from "@mui/icons-material/BlockRounded";
import UploadFileRoundedIcon        from "@mui/icons-material/UploadFileRounded";
import StorageRoundedIcon           from "@mui/icons-material/StorageRounded";
import DriveFileMoveRoundedIcon    from "@mui/icons-material/DriveFileMoveRounded";
import MoveToInboxRoundedIcon      from "@mui/icons-material/MoveToInboxRounded";
import TuneRoundedIcon             from "@mui/icons-material/TuneRounded";
import PersonRoundedIcon           from "@mui/icons-material/PersonRounded";
import PersonSearchRoundedIcon     from "@mui/icons-material/PersonSearchRounded";
import RestoreFromTrashRoundedIcon from "@mui/icons-material/RestoreFromTrashRounded";
import DeleteForeverRoundedIcon    from "@mui/icons-material/DeleteForeverRounded";
import DeleteSweepRoundedIcon      from "@mui/icons-material/DeleteSweepRounded";
import OutboxRoundedIcon           from "@mui/icons-material/OutboxRounded";
import { formatFileSize as formatSize } from "../../util/numberFormat";

import { useLocation, useHistory } from "react-router-dom";
import { mailApi, FOLDERS }        from "./api/mailApi";
import MailDetail                   from "./MailDetail";
import MailWriteModal                 from "./MailWriteModal";
import ContactsPanel                from "./components/contacts/ContactsPanel";
import ContactGroupPanel            from "./components/contacts/ContactGroupPanel";
import AdminMailPanel               from "./components/admin/AdminMailPanel";
import EmployeeMailboxPanel         from "./components/admin/EmployeeMailboxPanel";
import TablePaginator               from "../../component/common/TablePaginator";
import BlockListPanel               from "./components/contacts/BlockListPanel";
import MySignaturesPanel            from "./components/signature/MySignaturesPanel";
import MailMigrationPage            from "../mail-migration/MailMigrationPage";
import MailArchivePage              from "../mail-archive/MailArchivePage";
import AdminPromptPanel             from "./components/ai/AdminPromptPanel";
import AttachmentLibraryPanel       from "./components/compose/AttachmentLibraryPanel";
import BulkMailPanel                 from "./components/bulk/BulkMailPanel";
import ScheduledMailPanel            from "./components/scheduled/ScheduledMailPanel";
import DeliveryStatusBadge           from "./components/DeliveryStatusBadge";
import { ds }                       from "../../styles/designSystem";
import LoadingDots                  from "../../component/common/LoadingDots";
import ComboBox                     from "../../component/common/ComboBox";
import { notify }                   from "../../util/toast";
import { confirm }                  from "../../util/confirmDialog";
import { api as apiClient }         from "../../util/api";
import { rewriteImageUrls }         from "./components/compose/MailBodyHtml";
import dayjs                        from "dayjs";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const ICON_MAP = {
    ALL:     <AllInboxRoundedIcon      sx={{ fontSize: 18 }} />,
    INBOX:   <InboxRoundedIcon         sx={{ fontSize: 18 }} />,
    UNREAD:  <MarkEmailUnreadRoundedIcon sx={{ fontSize: 18 }} />,
    STARRED: <StarRoundedIcon          sx={{ fontSize: 18, color: "#f59e0b" }} />,
    SENT:    <SendRoundedIcon          sx={{ fontSize: 18 }} />,
    SELF:    <OutboxRoundedIcon        sx={{ fontSize: 18 }} />,
    DRAFT:   <DraftsRoundedIcon        sx={{ fontSize: 18 }} />,
    SCHEDULED: <ScheduleSendRoundedIcon sx={{ fontSize: 18 }} />,
    SPAM:    <ReportRoundedIcon        sx={{ fontSize: 18 }} />,
    TRASH:   <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />,
    ARCHIVE: <ArchiveRoundedIcon       sx={{ fontSize: 18 }} />,
};

/** 커스텀 폴더 선택 키 헬퍼 — folder 문자열에 "CUSTOM:{id}" 형태로 폴더 id 를 실어 나른다. */
const CUSTOM_PREFIX = "CUSTOM:";
const isCustomKey = (key) => typeof key === "string" && key.startsWith(CUSTOM_PREFIX);
const customIdOf = (key) => (isCustomKey(key) ? Number(key.slice(CUSTOM_PREFIX.length)) : null);
const customKey  = (id) => `${CUSTOM_PREFIX}${id}`;

/**
 * 내 폴더 접힘 상태 저장 키.
 *
 * <p>폴더 트리가 깊어지면 사이드바가 스크롤되기 시작해서 정작 자주 쓰는 기본 폴더·도구가 밀린다.
 * 접은 상태는 localStorage 에 남긴다 — 새로고침·재방문마다 다시 접는 건 그 자체가 짜증이다.
 * COLLAPSED 는 "접힌" id 집합이라 기본값(빈 집합)이 곧 기존 동작(전부 펼침)이 된다.
 */
const LS_COLLAPSED_KEY  = "mail_folder_collapsed";
const LS_MYFOLDERS_KEY  = "mail_myfolders_collapsed";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

/** 바이트 → "1.2 MB" 형식 */

/** 항상 "YY-MM-DD HH:mm" 형식으로 풀 날짜·시간 노출 */
function formatDate(t) {
    if (!t) return "";
    return dayjs(t).format("YY-MM-DD HH:mm");
}

/**
 * 접기/펼치기 애니메이션 래퍼 — grid-template-rows 0fr↔1fr.
 *
 * <p>MUI Collapse 를 쓰지 않는 이유: Collapse 는 자기 콘텐츠 높이를 <b>재서</b> 인라인 height 로
 * 박아 넣는다. 폴더 트리처럼 중첩되면 바깥이 높이를 재는 시점에 안쪽이 아직 0 이라 실제보다
 * 작은 값으로 굳고, 애니메이션이 끝나 overflow:visible 이 되는 순간 하위 폴더가 아래 섹션 위로
 * 새어 나온다(연락처·도구 줄과 겹침). 이 방식은 높이를 재지 않아 몇 겹으로 중첩돼도 안전하다.
 */
const CollapseRows = ({ open, children }) => (
    <Box sx={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 160ms ease",
        flexShrink: 0,
    }}>
        <Box sx={{ overflow: "hidden", minHeight: 0 }}>{children}</Box>
    </Box>
);

/**
 * 드롭 위치 판정 — 커서가 행의 «위/가운데/아래» 어디에 있는지.
 *
 * <p><b>«폴더 안»과 «폴더 사이»를 반드시 구분해야 한다.</b> 안으로만 되면 한번 들어간 폴더를
 * 밖으로 꺼낼 수 없고(메일플러그 이관 때 엉뚱한 폴더 하위로 들어간 그 문제), 사이로만 되면
 * 중첩을 만들 수 없다. 위/아래 28% 는 «사이»(형제로 삽입), 가운데 44% 는 «안»(하위로 이동).
 */
const dropZoneOf = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientY - r.top) / (r.height || 1);
    if (ratio < 0.28) return "before";
    if (ratio > 0.72) return "after";
    return "inside";
};

/**
 * 폴더 드래그 페이로드 MIME.
 * <p>드롭 판정 자체는 사이드바가 들고 있는 dragId 로 한다(같은 트리 안에서만 유효). 이 값은
 * 외부 앱·파일 드래그와 섞이지 않도록 페이로드에 표식을 남기는 용도다 — text/plain 만 실으면
 * 다른 곳에 떨궜을 때 폴더 이름이 텍스트로 붙는다.
 */
const DND_FOLDER_MIME = "application/x-mail-folder";

/**
 * 사용자 정의 폴더 1줄 — 트리(중첩) 재귀 렌더. 호버 시 ⋮ 메뉴(하위 폴더 추가/이름 변경/삭제).
 *
 * <p><b>모듈 최상위에 둬야 한다.</b> FolderSidebar 렌더 본문 안에서 정의하면 부모가 리렌더될
 * 때마다 컴포넌트 <i>타입</i>이 새로 만들어져 React 가 서브트리를 통째로 unmount→mount 한다.
 * 그러면 (1) 접기 애니메이션이 아예 재생되지 않고(새로 mount 된 노드는 이미 최종 상태),
 * (2) ⋮ 메뉴 anchor 같은 지역 state 가 날아간다. 필요한 값은 ctx 하나로 받는다.
 */
const CustomFolderItem = ({ folder, depth = 0, ctx }) => {
    const {
        view, active, customCounts, childrenByParent, collapsed,
        toggleCollapse, expandFolder,
        onSelectFolder, onCreateFolder, onRenameFolder, onDeleteFolder, onChangeFolderColor,
        dragId, dropHint, canDropOn, onDragStartFolder, onDragEndFolder, onDragOverFolder, onDropFolder,
    } = ctx;
    const [anchor, setAnchor] = useState(null);
    const isActive = view === "mail" && active === customKey(folder.id);
    const total  = customCounts?.total?.[folder.id] || 0;
    const children = childrenByParent.get(folder.id) || [];
    const hasChildren = children.length > 0;
    const isCollapsed = hasChildren && collapsed.has(folder.id);
    const isChild = depth > 0;   // 하위폴더는 아이콘/높이를 줄이고 연결 화살표를 붙인다

    /* ── 드래그&드롭 ─────────────────────────────────────────────
     * 자기 자신·자기 자손 위에는 아예 드롭을 허용하지 않는다(preventDefault 를 안 하면
     * 브라우저가 '금지' 커서를 띄워 준다). 서버도 순환을 막지만, 놓을 수 있는 것처럼
     * 보였다가 에러 토스트가 뜨는 건 조작 실패로 읽힌다.
     * ---------------------------------------------------------- */
    const isDragging = dragId === folder.id;
    const droppable  = dragId != null && canDropOn(folder.id);
    const hintZone   = (dropHint && dropHint.id === folder.id) ? dropHint.zone : null;
    const dropLine   = `inset 0 ${hintZone === "before" ? "3px" : "-3px"} 0 0 ${ds.colors.primary}`;

    return (
        <>
            <Box
                onClick={() => onSelectFolder(customKey(folder.id))}
                draggable
                onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.effectAllowed = "move";
                    // 일부 브라우저는 데이터가 비면 드래그를 시작조차 하지 않는다.
                    e.dataTransfer.setData(DND_FOLDER_MIME, String(folder.id));
                    e.dataTransfer.setData("text/plain", folder.name);
                    onDragStartFolder(folder.id);
                }}
                onDragEnd={onDragEndFolder}
                onDragOver={(e) => {
                    if (!droppable) return;               // preventDefault 생략 = 드롭 금지 커서
                    e.preventDefault();
                    e.stopPropagation();
                    e.dataTransfer.dropEffect = "move";
                    onDragOverFolder(folder.id, dropZoneOf(e));
                }}
                onDrop={(e) => {
                    if (!droppable) return;
                    e.preventDefault();
                    e.stopPropagation();
                    onDropFolder(folder, dropZoneOf(e));
                }}
                sx={{
                    display: "flex", alignItems: "center", gap: isChild ? 0.5 : 1.2,
                    pl: 1.2 + depth * 1.5, pr: 1.5, py: isChild ? 0.4 : 1, mx: 0.3, borderRadius: 1.2, cursor: "pointer",
                    bgcolor: hintZone === "inside" ? ds.colors.primaryBg
                           : isActive ? ds.colors.primaryBg : "transparent",
                    color:   isActive ? ds.colors.primaryText : "#475569",
                    fontWeight: isActive ? 700 : 500,
                    // «안»은 면(테두리)으로, «사이»는 선으로 — 같은 하이라이트를 쓰면 둘을 구분할 수 없다.
                    outline: hintZone === "inside" ? `2px dashed ${ds.colors.primary}` : "none",
                    outlineOffset: -2,
                    boxShadow: (hintZone === "before" || hintZone === "after") ? dropLine : "none",
                    opacity: isDragging ? 0.4 : 1,
                    "&:hover": { bgcolor: isActive ? ds.colors.primaryBg : "#f1f5f9",
                                 "& .folder-menu-btn": { opacity: 1 } },
                }}>
                {isChild && (
                    <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 14, color: "#cbd5e1", ml: -0.2, flexShrink: 0 }} />
                )}
                <FolderRoundedIcon sx={{ fontSize: isChild ? 15 : 18, color: folder.color || "#f59e0b", flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: isChild ? 12.5 : 13, flex: 1, color: "inherit", fontWeight: "inherit", minWidth: 0 }}>
                    {folder.name}
                </Typography>
                {/* 오른쪽 묶음 — 개수 · ⋮ · 접기 화살표.
                    행의 gap(최상위 9.6px)은 아이콘·이름 사이 간격이라 여기엔 너무 넓다. 따로 묶어
                    좁게 잡고, 접기 화살표는 <b>자식이 있을 때만</b> 자리를 만든다 — 없는 행까지 16px 를
                    비워 두면 늘 보이는 개수만 오른쪽 끝에서 밀린다. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, flexShrink: 0 }}>
                    {total > 0 && (
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, minWidth: 14, textAlign: "right" }}>
                            {total}
                        </Typography>
                    )}
                    <IconButton
                        className="folder-menu-btn"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
                        sx={{ p: 0.2, opacity: isActive ? 1 : 0, color: "#94a3b8", "&:hover": { color: "#475569" } }}>
                        <MoreVertRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    {hasChildren && (
                        <Tooltip title={isCollapsed ? `하위 폴더 ${children.length}개 펼치기` : "하위 폴더 접기"}>
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); toggleCollapse(folder.id); }}
                                sx={{ p: 0, flexShrink: 0, color: isActive ? "inherit" : "#94a3b8",
                                      "&:hover": { color: ds.colors.primary, bgcolor: "transparent" } }}>
                                <ExpandLessRoundedIcon
                                    sx={{ fontSize: 16, transition: "transform 160ms ease",
                                          transform: isCollapsed ? "rotate(180deg)" : "none" }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
                      onClick={(e) => e.stopPropagation()}>
                    <MenuItem onClick={() => { setAnchor(null); expandFolder(folder.id); onCreateFolder(folder.id); }}>
                        <CreateNewFolderRoundedIcon sx={{ fontSize: 16, mr: 1, color: "#64748b" }} /> 하위 폴더 추가
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchor(null); onRenameFolder(folder); }}>
                        <EditRoundedIcon sx={{ fontSize: 16, mr: 1, color: "#64748b" }} /> 이름 변경
                    </MenuItem>
                    {/* 색상은 메뉴 안에서 바로 고른다 — 폴더 하나 색 바꾸자고 별도 모달을 여는 건 과하다.
                        클릭 즉시 저장되고 사이드바 아이콘 색이 바뀌므로 결과를 그 자리에서 확인할 수 있다. */}
                    <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", mb: 0.8 }}>
                            폴더 색상
                        </Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 0.7 }}>
                            {FOLDER_COLORS.map(c => {
                                const isCurrent = (folder.color || DEFAULT_FOLDER_COLOR) === c;
                                return (
                                    <Tooltip key={c} title={isCurrent ? "현재 색상" : ""}>
                                        <Box
                                            onClick={() => { setAnchor(null); onChangeFolderColor(folder, c); }}
                                            sx={{
                                                width: 20, height: 20, borderRadius: "50%", bgcolor: c,
                                                cursor: "pointer", flexShrink: 0,
                                                // 선택된 색은 링으로 표시 — 색 위에 체크 아이콘을 얹으면
                                                // 밝은 색에서 안 보이고 어두운 색에서만 보인다.
                                                outline: isCurrent ? `2px solid ${c}` : "none",
                                                outlineOffset: 2,
                                                border: "1px solid rgba(0,0,0,0.08)",
                                                "&:hover": { transform: "scale(1.15)" },
                                                transition: "transform .12s",
                                            }}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>
                    <MenuItem onClick={() => { setAnchor(null); onDeleteFolder(folder); }}>
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16, mr: 1, color: "#dc2626" }} /> 삭제
                    </MenuItem>
                </Menu>
            </Box>
            {hasChildren && (
                <CollapseRows open={!isCollapsed}>
                    {children.map(cf => <CustomFolderItem key={cf.id} folder={cf} depth={depth + 1} ctx={ctx} />)}
                </CollapseRows>
            )}
        </>
    );
};

/**
 * 접힘(48px) 레일의 폴더 1칸 — 아이콘 · 항목 색 · Tooltip 만 남는다.
 *
 * <p><b>Tooltip 이 필수다.</b> 이름 텍스트가 사라지면 사용자 폴더는 전부 같은 폴더 아이콘이라
 * 색을 바꾸지 않은 폴더가 둘만 있어도 구분이 불가능해진다.
 *
 * <p>안 읽음 뱃지는 펼친 사이드바와 함께 제거했다 — 접힌 레일에만 남기면 "접으면 숫자가 생기고
 * 펼치면 없어지는" 규칙이 되어 오히려 설명이 필요해진다. 안 읽은 메일은 레일의 '안 읽은 메일'
 * 아이콘을 눌러 본다. 전체 개수와 ⋮ 메뉴는 원래도 48px 안에서 읽히지 않아 없다.
 *
 * <p>펼치기는 여기서 부르지 않는다. CollapsibleSidebar 의 레일 컨테이너가 버블 단계에서
 * 받아 처리하므로 "선택 + 펼침"이 저절로 성립한다.
 */
const CollapsedFolderIcon = ({ icon, label, isActive, onClick }) => (
    <Tooltip title={label} placement="right">
        <Box
            onClick={onClick}
            sx={{
                width: 34, height: 34, mb: 0.3, borderRadius: 1.2, flexShrink: 0, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: isActive ? ds.colors.primaryBg : "transparent",
                color:   isActive ? ds.colors.primaryText : "#475569",
                "&:hover": { bgcolor: isActive ? ds.colors.primaryBg : "#f1f5f9" },
            }}>
            {icon}
        </Box>
    </Tooltip>
);

/* ──────────────────────────────────────────────────────────────────
 *  사이드바
 *  ────────────────────────────────────────────────────────────── */
function FolderSidebar({
    active, view,
    onSelectFolder, onShowContacts, contactScope, onShowBlockList, onShowGroups,
    onShowAdminAll, onShowAdminUnmatched, onShowAdminMailbox,
    onShowSignatures, onShowAiPrompt, onShowMigration, onShowAttachments, onShowArchive,
    canArchive,
    canViewOthersMail,
    isAdmin,
    unreadCounts, totalCounts, starredCount, onCompose, onComposeSelf,
    customFolders, customCounts, onCreateFolder, onRenameFolder, onDeleteFolder, onChangeFolderColor,
    onMoveFolder,
}) {
    /**
     * 사이드바 항목 1줄
     *
     * count       — 안 읽은 메일 수 (파란 뱃지, primary 색)
     * totalCount  — 전체 메일 수 (회색 작은 글씨)
     *
     * 표시 규칙
     *   - 안 읽음 0 일 때: "받은편지함  123" (전체만 회색)
     *   - 안 읽음 N 있을 때: "받은편지함  [N]  123" (파란 뱃지 + 회색 전체)
     */
    const GroupLabel = ({ children }) => (
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.04em", px: 1.8, pt: 1.4, pb: 0.4 }}>
            {children}
        </Typography>
    );

    /**
     * 사이드바 항목 오른쪽 회색 숫자 — <b>그 폴더에 든 개수</b>. 안 읽음 강조 뱃지는 두지 않는다.
     *
     * <p>안 읽은 메일은 '안 읽은 메일' 항목 하나로 본다. 폴더마다 파란 뱃지를 흩뿌리면
     * 시선이 계속 끌리는데 정작 할 일은 "안 읽은 걸 연다" 하나뿐이라, 표시할 이유가 없다.
     *
     * <p>'UNREAD' 는 진짜 폴더가 아니라 받은메일함의 부분집합이라 total-counts 에 키가 없다.
     * 그 항목의 개수 = 안 읽은 메일 수이므로 {@code unreadCounts.INBOX} 를 빌려 쓴다
     * (백엔드 findUnreadInbox 가 INBOX 로 한정된 이유이기도 하다). 다른 폴더와 같은 회색이다 —
     * 여기서만 파랗게 칠하면 결국 안 읽음 뱃지를 자리만 옮겨 되살리는 꼴이 된다.
     *
     * <p>좌측 전역 네비의 '메일' 뱃지는 그대로 둔다 — 메일 화면 <b>밖</b>에서는 그게 유일한 신호다.
     */
    const itemCount = (key) =>
        (key === "UNREAD"  ? unreadCounts?.INBOX
        : key === "STARRED" ? starredCount
        : totalCounts?.[key]) || 0;

    /* ── 폴더 트리 접기/펼치기 ───────────────────────────────────────
     * 접힘 상태는 FolderSidebar 가 들고 있는다. CustomFolderItem 은 렌더 본문 안에서
     * 정의되는 컴포넌트라 부모가 리렌더될 때마다 타입이 새로 만들어져 지역 state 가 날아간다.
     * ------------------------------------------------------------ */
    const [collapsed, setCollapsed] = useState(() => {
        try {
            const raw = localStorage.getItem(LS_COLLAPSED_KEY);
            return new Set(raw ? JSON.parse(raw) : []);
        } catch { return new Set(); }
    });

    const persistCollapsed = (set) => {
        try { localStorage.setItem(LS_COLLAPSED_KEY, JSON.stringify([...set])); } catch { /* 저장 실패는 무시 */ }
    };

    const toggleCollapse = useCallback((id) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            persistCollapsed(next);
            return next;
        });
    }, []);

    /** 하위 폴더를 새로 만들 때처럼 "결과가 보여야 하는" 순간에 강제로 펼친다. */
    const expandFolder = useCallback((id) => {
        setCollapsed(prev => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            persistCollapsed(next);
            return next;
        });
    }, []);

    /** 내 폴더 섹션 전체 접기 — 폴더를 많이 만든 사람은 목록째로 치우고 싶어 한다. */
    const [myFoldersOpen, setMyFoldersOpen] = useState(() => {
        try { return localStorage.getItem(LS_MYFOLDERS_KEY) !== "1"; } catch { return true; }
    });
    const toggleMyFolders = useCallback(() => {
        setMyFoldersOpen(prev => {
            const next = !prev;
            try { localStorage.setItem(LS_MYFOLDERS_KEY, next ? "0" : "1"); } catch { /* 무시 */ }
            return next;
        });
    }, []);

    /** parentId → 자식 목록. 행마다 filter 를 돌리면 폴더 수의 제곱이 된다. */
    const childrenByParent = useMemo(() => {
        const m = new Map();
        (customFolders || []).forEach(f => {
            const k = f.parentId || null;
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(f);
        });
        return m;
    }, [customFolders]);

    /* 안 읽음 합계(subtreeUnread / allCustomUnread)는 제거했다 — 사이드바에서 폴더별 안 읽음을
       더는 표시하지 않는다. 안 읽은 메일은 '안 읽은 메일' 항목 한 곳에서만 본다. */

    /** 선택된 폴더가 접힌 부모 안에 있으면 조상을 자동으로 펼친다 — 골랐는데 안 보이면 길을 잃는다. */
    useEffect(() => {
        if (!isCustomKey(active)) return;
        const byId = new Map((customFolders || []).map(f => [f.id, f]));
        const path = [];
        let cur = byId.get(customIdOf(active));
        while (cur?.parentId && byId.has(cur.parentId) && path.length < 50) {
            path.push(cur.parentId);
            cur = byId.get(cur.parentId);
        }
        if (!path.length) return;
        setCollapsed(prev => {
            if (!path.some(id => prev.has(id))) return prev;   // 이미 다 펼쳐져 있으면 건드리지 않는다
            const next = new Set(prev);
            path.forEach(id => next.delete(id));
            persistCollapsed(next);
            return next;
        });
    }, [active, customFolders]);

    /* ── 폴더 드래그&드롭 ────────────────────────────────────────
     * 드래그 상태는 사이드바가 하나만 들고 있는다. 행마다 들고 있으면 «지금 무엇을 끌고 있는지»를
     * 다른 행이 알 수 없어 드롭 가능 여부·하이라이트를 그릴 수 없다.
     *   dragId   — 끌고 있는 폴더 id
     *   dropHint — { id: 폴더id | "ROOT", zone: "before"|"inside"|"after" }
     * ---------------------------------------------------------- */
    const [dragId, setDragId] = useState(null);
    const [dropHint, setDropHint] = useState(null);

    const onDragStartFolder = useCallback((id) => { setDragId(id); setDropHint(null); }, []);
    const onDragEndFolder   = useCallback(() => { setDragId(null); setDropHint(null); }, []);
    const onDragOverFolder  = useCallback((id, zone) => {
        // 같은 값이면 setState 를 건너뛴다 — dragover 는 초당 수십 번 뜨고, 그때마다 트리를
        // 다시 그리면 드래그가 눈에 띄게 끊긴다.
        setDropHint(prev => (prev && prev.id === id && prev.zone === zone) ? prev : { id, zone });
    }, []);

    /** 순환 방지(화면 쪽 1차) — 자기 자신/자손 위에는 놓을 수 없다. 최종 판정은 서버가 한다. */
    const canDropOn = useCallback((targetId) => {
        if (dragId == null || targetId == null) return false;
        if (dragId === targetId) return false;
        const stack = [...(childrenByParent.get(dragId) || [])];
        let guard = 0;
        while (stack.length && guard++ < 1000) {
            const node = stack.pop();
            if (node.id === targetId) return false;          // 자손 위 = 순환
            (childrenByParent.get(node.id) || []).forEach(c => stack.push(c));
        }
        return true;
    }, [dragId, childrenByParent]);

    /**
     * 드롭 확정 — «안»이면 하위로, «사이»면 대상과 같은 부모의 그 자리로.
     *
     * <p>sortOrder 는 <b>끌고 있는 폴더를 뺀</b> 형제 목록 기준 위치다. 서버도 대상 폴더를
     * 목록에서 제거한 뒤 그 자리에 끼워넣으므로 계산이 어긋나지 않는다.
     */
    const onDropFolder = useCallback((target, zone) => {
        const id = dragId;
        setDragId(null); setDropHint(null);
        if (id == null || !target || id === target.id) return;

        if (zone === "inside") {
            expandFolder(target.id);                          // 넣었는데 접혀 있으면 사라진 것처럼 보인다
            onMoveFolder(id, { parentId: target.id, sortOrder: null });
            return;
        }
        const parentId = target.parentId || null;
        const siblings = (childrenByParent.get(parentId) || []).filter(f => f.id !== id);
        const pos = siblings.findIndex(f => f.id === target.id);
        onMoveFolder(id, { parentId, sortOrder: pos < 0 ? null : (zone === "after" ? pos + 1 : pos) });
    }, [dragId, childrenByParent, expandFolder, onMoveFolder]);

    /** «내 폴더» 헤더로 끌어다 놓으면 최상위로 분리 — 깊이 5단계 안쪽에서 한 번에 꺼내는 탈출구. */
    const rootDropActive = dropHint?.id === "ROOT";
    const rootDropProps = {
        onDragOver: (e) => {
            if (dragId == null) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            setDropHint(prev => (prev?.id === "ROOT") ? prev : { id: "ROOT", zone: "inside" });
        },
        onDrop: (e) => {
            if (dragId == null) return;
            e.preventDefault();
            const id = dragId;
            setDragId(null); setDropHint(null);
            onMoveFolder(id, { parentId: null, sortOrder: null });
        },
    };

    const SidebarItem = ({ icon, label, totalCount, isActive, onClick }) => (
        <Box
            onClick={onClick}
            sx={{
                display: "flex", alignItems: "center", gap: 1.2,
                px: 1.5, py: 1, mx: 0.3, borderRadius: 1.2, cursor: "pointer",
                bgcolor: isActive ? ds.colors.primaryBg : "transparent",
                color:   isActive ? ds.colors.primaryText : "#475569",
                fontWeight: isActive ? 700 : 500,
                "&:hover": { bgcolor: isActive ? ds.colors.primaryBg : "#f1f5f9" },
            }}>
            {icon}
            <Typography sx={{ fontSize: 13, flex: 1, color: "inherit", fontWeight: "inherit" }}>{label}</Typography>
            {totalCount > 0 && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500, minWidth: 16, textAlign: "right" }}>
                    {totalCount}
                </Typography>
            )}
        </Box>
    );

    /** CustomFolderItem 이 모듈 최상위 컴포넌트라 필요한 값은 이 묶음으로 내려준다. */
    const folderCtx = useMemo(() => ({
        view, active, customCounts, childrenByParent, collapsed,
        toggleCollapse, expandFolder,
        onSelectFolder, onCreateFolder, onRenameFolder, onDeleteFolder, onChangeFolderColor,
        dragId, dropHint, canDropOn, onDragStartFolder, onDragEndFolder, onDragOverFolder, onDropFolder,
    }), [view, active, customCounts, childrenByParent, collapsed,
         toggleCollapse, expandFolder,
         onSelectFolder, onCreateFolder, onRenameFolder, onDeleteFolder, onChangeFolderColor,
         dragId, dropHint, canDropOn, onDragStartFolder, onDragEndFolder, onDragOverFolder, onDropFolder]);

    /**
     * 접힘 레일 내용 — 시스템 폴더 + <b>최상위</b> 사용자 폴더.
     *
     * <p>하위 폴더는 그리지 않는다. 48px 에 들여쓰기 트리를 욱여넣으면 depth 를 읽을 수 없다.
     * 펼치면 바로 보이므로, 여기서는 최상위만 두고 나머지는 펼치기에 맡긴다.
     */
    const renderCollapsedRail = () => {
        const roots = childrenByParent.get(null) || [];
        return (
            <Stack sx={{ flex: 1, minHeight: 0, overflowY: "auto", alignItems: "center", pt: 0.4, pb: 1,
                         // 48px 폭에선 스크롤바가 아이콘을 밀어낸다. 자리를 아예 주지 않는다.
                         scrollbarWidth: "none", "&::-webkit-scrollbar": { width: 0 } }}>
                {FOLDERS.filter(f => f.key).map(f => (
                    <CollapsedFolderIcon
                        key={f.key}
                        icon={ICON_MAP[f.key]}
                        label={f.label}
                        isActive={view === "mail" && active === f.key}
                        onClick={() => onSelectFolder(f.key)}
                    />
                ))}
                {roots.length > 0 && (
                    <Box sx={{ width: 22, borderTop: "1px solid #e2e8f0", my: 0.6, flexShrink: 0 }} />
                )}
                {roots.map(f => (
                    <CollapsedFolderIcon
                        key={f.id}
                        icon={<FolderRoundedIcon sx={{ fontSize: 18, color: f.color || DEFAULT_FOLDER_COLOR }} />}
                        label={f.name}
                        isActive={view === "mail" && active === customKey(f.id)}
                        onClick={() => onSelectFolder(customKey(f.id))}
                    />
                ))}
            </Stack>
        );
    };

    return (
        <CollapsibleSidebar
            storageKey="mail.folder"
            width={220}
            /* 다른 세 화면(기본 1200)보다 높게 잡는다 — 메일만 본문이 «표» 라서 최소 폭이 크다(≈1011px).
               1440 노트북에서 메인 네비(290) + 이 사이드바(220) 를 다 펼치면 본문에 930px 만 남아
               표가 가로로 넘친다. 기본값 1200 이면 그 구간이 자동 접기 사각지대라, 사용자가 스스로
               접기 전까지 가로 스크롤을 본다. 맥북 14/16 의 기본 해상도가 정확히 여기 걸린다.
               1500 이면 1440 은 접힌 채 뜨고(48px → 본문 1102px), 1600 이상은 지금처럼 펼쳐진 채 뜬다. */
            autoCollapseBelow={1500}
            expandTitle="폴더 목록 펼치기"
            collapseTitle="폴더 목록 접기"
            renderCollapsed={renderCollapsedRail}
            sx={{ borderRight: "1px solid #e2e8f0", bgcolor: "#fafbfc" }}
        >
            {/* 작성 버튼 — 메일쓰기 + 내게쓰기 */}
            <Box sx={{ p: 1.5, display: "flex", gap: 0.6 }}>
                <ActionButton
                    variant="primary"
                    startIcon={<EditRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={onCompose}
                    label="메일 쓰기"
                    sx={{ flex: 1, py: 1 }}
                />
                <Tooltip title="나에게 메일 보내기">
                    <IconButton
                        onClick={onComposeSelf}
                        sx={{
                            border: "1px solid #e2e8f0", borderRadius: 1.5,
                            color: "#64748b", bgcolor: "#fff",
                            "&:hover": { bgcolor: "#f1f5f9", color: ds.colors.primary },
                        }}>
                        <PersonRoundedIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* scrollbarGutter: stable — 폴더를 펼쳐 내용이 길어지는 순간 스크롤바가 생겼다 사라지면서
                안쪽 폭이 들쭉날쭉해진다(토글할 때마다 사이드바 가로가 튀어 보이던 원인).
                자리를 항상 잡아두고, 스크롤바는 좌측 메인 사이드바와 같은 4px 로 맞춘다. */}
            <Stack sx={{ flex: 1, overflowY: "auto", scrollbarGutter: "stable", px: 0.5, pb: 1,
                         "&::-webkit-scrollbar": { width: 4 },
                         "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.1)", borderRadius: 99 } }}>
                {FOLDERS.map((f, i) => (
                    f.type === "group" ? (
                        <GroupLabel key={`grp-${i}`}>{f.label}</GroupLabel>
                    ) : (
                        <SidebarItem
                            key={f.key}
                            icon={ICON_MAP[f.key]}
                            label={f.label}
                            totalCount={itemCount(f.key)}
                            isActive={view === "mail" && active === f.key}
                            onClick={() => onSelectFolder(f.key)}
                        />
                    )
                ))}

                {/* ─── 내 폴더 (사용자 정의) ─── */}
                <Box
                    onClick={toggleMyFolders}
                    {...rootDropProps}
                    sx={{ display: "flex", alignItems: "center", px: 1.8, pt: 1.6, pb: 0.4,
                          cursor: "pointer", borderRadius: 1.2, mx: 0.3,
                          bgcolor: rootDropActive ? ds.colors.primaryBg : "transparent",
                          outline: rootDropActive ? `2px dashed ${ds.colors.primary}` : "none",
                          outlineOffset: -2,
                          "&:hover": { "& .myfolders-label": { color: "#64748b" } } }}>
                    <Typography className="myfolders-label"
                        sx={{ fontSize: 10.5, fontWeight: 800,
                              color: rootDropActive ? ds.colors.primaryText : "#94a3b8",
                              letterSpacing: "0.04em", flex: 1 }}>
                        {rootDropActive ? "여기에 놓으면 최상위로 분리" : "내 폴더"}
                    </Typography>
                    <Tooltip title="새 폴더 만들기">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); if (!myFoldersOpen) toggleMyFolders(); onCreateFolder(); }}
                            sx={{ p: 0.2, color: "#94a3b8", "&:hover": { color: ds.colors.primary } }}>
                            <CreateNewFolderRoundedIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                    <ExpandLessRoundedIcon
                        sx={{ fontSize: 14, color: "#94a3b8", ml: 0.4,
                              transition: "transform 160ms ease",
                              transform: myFoldersOpen ? "none" : "rotate(180deg)" }} />
                </Box>
                <CollapseRows open={myFoldersOpen}>
                    {(childrenByParent.get(null) || []).map((f) => (
                        <CustomFolderItem key={f.id} folder={f} depth={0} ctx={folderCtx} />
                    ))}
                    {(!customFolders || customFolders.length === 0) && (
                        <Typography sx={{ fontSize: 11.5, color: "#cbd5e1", px: 1.8, py: 0.5 }}>
                            폴더를 만들어 메일을 정리하세요.
                        </Typography>
                    )}
                </CollapseRows>

                {/* ─── 연락처 ─── 도구보다 위. 메일을 쓰다 주소를 찾는 흐름이라 도구(서명·차단 등)보다 훨씬 자주 쓴다.
                     예전엔 '내 연락처' 하나가 도구 안에 묻혀 있어 공용 주소록 3천여 건으로 가는 길이 없었다. */}
                <Box sx={{ borderTop: "1px solid #e2e8f0", mt: 1, pt: 0.5 }} />
                <GroupLabel>연락처</GroupLabel>
                <SidebarItem
                    icon={<ContactsRoundedIcon sx={{ fontSize: 18 }} />}
                    label="내 연락처"
                    isActive={view === "contacts" && contactScope === "PERSONAL"}
                    onClick={() => onShowContacts("PERSONAL")}
                />
                {/* 정확히 그 탭일 때만 활성 — 예전엔 '개인이 아니면 공용' 이라 사내연락처·수신 그룹에
                    있을 때도 공용이 켜져 있었다. 항목이 셋이 된 지금은 그냥 틀린 표시다. */}
                <SidebarItem
                    icon={<GroupsRoundedIcon sx={{ fontSize: 18 }} />}
                    label="공용 연락처"
                    isActive={view === "contacts" && contactScope === "PUBLIC"}
                    onClick={() => onShowContacts("PUBLIC")}
                />

                <SidebarItem
                    icon={<GroupAddRoundedIcon sx={{ fontSize: 18 }} />}
                    label="메일 수신 그룹"
                    isActive={view === "groups"}
                    onClick={onShowGroups}
                />

                <Box sx={{ borderTop: "1px solid #e2e8f0", mt: 1, pt: 0.5 }} />
                <GroupLabel>도구</GroupLabel>
                {/* 수신 그룹은 주소록이 아니라 <b>발송 묶음</b>이라 연락처가 아니라 도구에 선다 —
                    서명·차단 목록처럼 '메일을 쓸 때 쓰는 설정' 쪽이 성격이 맞다. 목록 맨 위에 두는 건
                    셋 중 작성 흐름에 가장 자주 끼어드는 것이라서. */}

                <SidebarItem
                    icon={<DrawRoundedIcon sx={{ fontSize: 18 }} />}
                    label="서명 설정"
                    isActive={view === "signatures"}
                    onClick={onShowSignatures}
                />
                <SidebarItem
                    icon={<AttachFileRoundedIcon sx={{ fontSize: 18 }} />}
                    label="첨부 파일"
                    isActive={view === "attachments"}
                    onClick={onShowAttachments}
                />
                <SidebarItem
                    icon={<BlockRoundedIcon sx={{ fontSize: 18 }} />}
                    label="차단 목록"
                    isActive={view === "block-list"}
                    onClick={onShowBlockList}
                />
                <SidebarItem
                    icon={<UploadFileRoundedIcon sx={{ fontSize: 18 }} />}
                    label="외부 이메일 가져오기"
                    isActive={view === "migration"}
                    onClick={onShowMigration}
                />

                {/* ─── 설정 섹션 ─── */}
                {/*<Box sx={{ px: 1.8, pt: 1.6, pb: 0.4 }}>*/}
                {/*    <Typography sx={{*/}
                {/*        fontSize: 10.5, fontWeight: 800, color: "#94a3b8",*/}
                {/*        letterSpacing: "0.04em",*/}
                {/*    }}>*/}
                {/*        설정*/}
                {/*    </Typography>*/}
                {/*</Box>*/}
                {isAdmin && (
                    <>
                        <Box sx={{ borderTop: "1px solid #e2e8f0", mt: 1, pt: 0.5 }} />
                        <GroupLabel>관리자</GroupLabel>
                        {/* 아래 세 개는 남의 메일 본문·첨부를 그대로 열람하는 기능이라 관리자 전체가 아니라
                            지정 담당자 계정만 본다. 노출 여부는 서버(/mail/admin/capabilities)만 따른다 —
                            화면에서 계정을 다시 계산하면 서버 규칙과 언젠가 어긋난다. */}
                        {canViewOthersMail && (
                            <>
                                {/* 결재 관리자 모드처럼 회사 전체 메일 열람 — 행 클릭 시 READ_OTHER 감사 기록 */}
                                <SidebarItem
                                    icon={<AllInboxRoundedIcon sx={{ fontSize: 18 }} />}
                                    label="전체 메일"
                                    isActive={view === "admin-all"}
                                    onClick={onShowAdminAll}
                                />
                                {/* 특정 직원 한 사람의 편지함을 그 사람 폴더 구성 그대로 — '전체 메일' 로는
                                    '이 사람이 그 메일을 어디에 뒀는지(스팸·휴지통·미열람)' 를 알 수 없다 */}
                                <SidebarItem
                                    icon={<PersonSearchRoundedIcon sx={{ fontSize: 18 }} />}
                                    label="직원 메일함"
                                    isActive={view === "admin-mailbox"}
                                    onClick={onShowAdminMailbox}
                                />
                                {/* 아무 받은편지함에도 배정 안 된 수신 메일 — 수동 배정(reassign) 대상 */}
                                <SidebarItem
                                    icon={<MarkEmailUnreadRoundedIcon sx={{ fontSize: 18 }} />}
                                    label="미배정 메일"
                                    isActive={view === "admin-unmatched"}
                                    onClick={onShowAdminUnmatched}
                                />
                            </>
                        )}
                        <SidebarItem
                            icon={<TuneRoundedIcon sx={{ fontSize: 18 }} />}
                            label="AI 프롬프트"
                            isActive={view === "ai-prompt"}
                            onClick={onShowAiPrompt}
                        />
                        {/* NAS 대량 이관 — 노출 여부는 서버 판단(/mail-archive/available)만 따른다.
                            화면에서 계정·권한을 다시 계산하면 서버 규칙과 언젠가 어긋난다. */}
                        {canArchive && (
                            <SidebarItem
                                icon={<StorageRoundedIcon sx={{ fontSize: 18 }} />}
                                label="NAS 메일 이관"
                                isActive={view === "mail-archive"}
                                onClick={onShowArchive}
                            />
                        )}
                    </>
                )}

            </Stack>
        </CollapsibleSidebar>
    );
}

/* ──────────────────────────────────────────────────────────────────
 *  툴바 — 일괄 작업 + 정렬·페이지 크기
 *  ────────────────────────────────────────────────────────────── */
function MailToolbar({
    selectedIds, allChecked, indeterminate, onToggleAll,
    folder, searchMode, pageSize, onPageSize, sortDesc, onToggleSort,
    onBulkDelete, onBulkSpam, onBulkUnspam, onBulkRestore, onBulkMarkRead, onBulkMarkUnread,
    onBulkMove, onBulkMoveToFolder, onBulkDeleteForever, onEmptySpam, customFolders,
    blockedMoveTargets = [],
}) {
    const [moveAnchor, setMoveAnchor] = useState(null);

    const has = selectedIds.length > 0;
    /**
     * 폴더 종속 액션(스팸 등록·해제, 복원, 영구 삭제, 스팸함 비우기)은 <b>검색 모드에서 숨긴다</b>.
     *
     * <p>검색은 폴더 무관(전체 메일)인데 툴바만 사이드바 폴더 기준이면, 스팸함에서 검색한 결과에
     * 받은 메일함 메일이 섞여 있어도 "스팸 해제" 가 떠서 무관한 폴더의 메일이 통째로 옮겨진다.
     * 대상 폴더를 사용자가 직접 고르는 액션(삭제·이동)과 폴더 무관 액션(읽음/안읽음)만 남긴다.
     */
    const isTrash = folder === "TRASH" && !searchMode;
    const isSpam  = folder === "SPAM"  && !searchMode;
    /** 예약함 — 서버가 status=SCHEDULED 메일의 폴더 이동을 전부 거부하므로 이동/삭제 계열을 숨긴다. */
    const isScheduled = folder === "SCHEDULED" && !searchMode;

    /**
     * 일괄 액션 버튼:
     *  - 선택 없을 때(비활성)도 회색 disabled 처리 안 하고 평범한 검정 톤으로 노출 (시인성 확보)
     *  - 선택이 있으면 디자인 시스템의 primary 파란색 + 굵게 (활성화 강조)
     *  - 클릭 자체는 has=false 면 noop 처리
     */
    const Btn = ({ icon, label, onClick, danger, tooltip }) => {
        const activeColor   = danger ? "#dc2626" : ds.colors.primary;
        const inactiveColor = danger ? "#9b1c1c" : "#0f172a";
        return (
            <ActionButton
                variant="secondary"
                startIcon={icon}
                tooltip={tooltip}
                onClick={() => { if (has) onClick?.(); }}
                sx={{
                    fontSize: 12.5,
                    fontWeight: has ? 700 : 500,
                    color: has ? activeColor : inactiveColor,
                    px: 1.2, minWidth: "auto",
                    borderColor: "transparent",
                    "&:hover": {
                        borderColor: "transparent",
                        color: has ? activeColor : inactiveColor,
                        bgcolor: has
                            ? (danger ? "#fee2e2" : ds.colors.primaryBg)
                            : "#f1f5f9",
                    },
                }}
                label={label}
            />
        );
    };

    /**
     * '삭제'(= 휴지통으로 이동) — 스팸함과 그 밖의 폴더가 <b>같은 버튼</b>을 쓴다.
     *
     * <p>두 자리에 각각 써 두면 라벨이나 툴팁을 한쪽만 고치는 날이 온다. 특히 이 버튼은
     * "영구 삭제가 아니다" 를 툴팁으로 알리는 게 요점이라, 두 화면의 설명이 갈리면
     * 한쪽 사용자만 되돌릴 수 있다는 사실을 모르게 된다.
     */
    const deleteToTrashBtn = (
        <Btn icon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />} label="삭제"
             onClick={onBulkDelete} danger
             tooltip="휴지통으로 옮깁니다 — 영구 삭제가 아니라 휴지통에서 다시 복원할 수 있습니다" />
    );

    return (
        <Box sx={{
            display: "flex", alignItems: "center", gap: 0.4,
            px: 2, py: 1, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff",
            flexShrink: 0,
        }}>
            <Checkbox
                size="small"
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={onToggleAll}
                sx={{ p: 0.5, color: "#cbd5e1", "&.Mui-checked": { color: ds.colors.primary } }}
            />

            {isTrash ? (
                /* 휴지통에서 할 일은 둘뿐이다 — 되살리거나, 완전히 지우거나.
                   '복원' 은 목록의 유형 칸에 적힌 그 폴더로 되돌아간다(서버가 previous_folder 를 본다).
                   폴더를 직접 고르는 '이동' 을 함께 두면 "복원과 뭐가 다르지" 를 매번 판단하게 되고,
                   실수로 고른 엉뚱한 폴더는 되돌릴 근거(previous_folder)가 이미 소진돼 복구가 어렵다. */
                <>
                    <Btn icon={<RestoreFromTrashRoundedIcon sx={{ fontSize: 16 }} />} label="복원"
                         onClick={onBulkRestore}
                         tooltip="선택한 메일을 원래 있던 폴더로 되돌립니다 (목록의 '유형' 칸에 적힌 곳)" />
                    <Btn icon={<DeleteForeverRoundedIcon sx={{ fontSize: 16 }} />} label="영구 삭제"
                         onClick={onBulkDeleteForever} danger
                         tooltip="내 메일함에서 완전히 사라집니다. 되돌릴 수 없습니다" />
                </>
            ) : isSpam ? (
                /* 스팸함도 할 일은 셋뿐이다 — 되돌리거나(스팸 해제), 휴지통으로 치우거나, 통째로 비우거나.
                   '스팸 해제' 는 휴지통의 '복원' 과 같은 동작이다(둘 다 batchRestore → previous_folder).
                   폴더를 직접 고르는 '이동' 을 함께 두지 않는 이유도 같다 — 이동하는 순간 previous_folder 가
                   덮여 원래 자리로 되돌릴 근거가 사라진다. («스팸이 아니라 특정 폴더로 정리» 는 스팸 해제 후
                   그 폴더에서 옮기면 된다. 한 단계 더 들지만 되돌릴 수 없는 실수를 만들지 않는다.)

                   순서는 휴지통과 맞춘다 — 되살리는 쪽이 왼쪽, 치우는 쪽이 오른쪽. 두 화면에서 같은 성격의
                   버튼이 같은 자리에 있어야 손이 기억한다(예전엔 스팸함만 '삭제' 가 맨 앞이었다). */
                <>
                    <Btn icon={<MoveToInboxRoundedIcon sx={{ fontSize: 16 }} />} label="스팸 해제"
                         onClick={onBulkUnspam}
                         tooltip="선택한 메일을 스팸이 아닌 것으로 되돌립니다 — 원래 있던 폴더로 갑니다" />
                    {deleteToTrashBtn}
                    {/* 스팸함 비우기 — 선택과 무관하게 '현재 목록 전체'를 휴지통으로 보낸다(영구 삭제 아님).
                        그래서 has(선택 여부) 게이팅을 받는 Btn 을 쓰지 않고 별도 버튼으로 둔다. */}
                    <ActionButton
                        variant="secondary"
                        startIcon={<DeleteSweepRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={() => onEmptySpam?.()}
                        tooltip="선택과 무관하게 현재 목록 전체를 휴지통으로 보냅니다 (영구 삭제 아님)"
                        sx={{
                            fontSize: 12.5, fontWeight: 500, color: "#9b1c1c",
                            px: 1.2, minWidth: "auto", borderColor: "transparent",
                            "&:hover": { borderColor: "transparent", color: "#9b1c1c", bgcolor: "#fee2e2" },
                        }}
                        label="스팸함 비우기"
                    />
                </>
            ) : isScheduled ? null : (
                /* 예약함은 삭제 버튼을 내린다 — 서버 이동 가드가 status=SCHEDULED 메일의 폴더 이동을
                   전부 거부하므로(폴더만 옮겨도 디스패처가 그대로 발송) 눌러도 전건 실패한다.
                   취소는 상세 화면의 '예약 취소' 가 담당한다. */
                deleteToTrashBtn
            )}

            {!isSpam && !isTrash && !isScheduled && !searchMode && (
                <Btn icon={<ReportRoundedIcon sx={{ fontSize: 16 }} />} label="스팸 등록" onClick={onBulkSpam} />
            )}

            {/* 읽음/안읽음은 휴지통·스팸함에서 뺀다 — 버리거나 걸러낸 메일의 읽음 상태는 이 시스템에서
                아무 데도 쓰이지 않는다. 사이드바 안읽음 뱃지는 받은메일함만 세고(READ_STATE_FOLDERS),
                목록도 이 두 폴더에서는 흐림 처리를 하지 않는다. 아무것도 바뀌지 않는 버튼이
                파괴적 버튼(영구 삭제·스팸함 비우기) 옆에 놓여 있으면 그쪽으로 손이 가는 사고만 늘린다. */}
            {!isTrash && !isSpam && (
                <>
                    <Btn icon={<MarkEmailReadRoundedIcon   sx={{ fontSize: 16 }} />} label="읽음 처리"   onClick={onBulkMarkRead} />
                    <Btn icon={<MarkEmailUnreadRoundedIcon sx={{ fontSize: 16 }} />} label="안읽음 처리" onClick={onBulkMarkUnread} />
                </>
            )}

            {/* 예약함은 이동 자체가 서버에서 거부되므로 메뉴를 열지 않는다.
                휴지통·스팸함은 '복원'·'스팸 해제' 가 이동을 대신한다 — 위 isTrash/isSpam 블록 주석 참고. */}
            {!isScheduled && !isTrash && !isSpam && <ActionButton
                variant="secondary"
                startIcon={<DriveFileMoveRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={(e) => { if (has) setMoveAnchor(e.currentTarget); }}
                sx={{
                    fontSize: 12.5,
                    fontWeight: has ? 700 : 500,
                    color: has ? ds.colors.primary : "#0f172a",
                    px: 1.2, minWidth: "auto", borderColor: "transparent",
                    "&:hover": {
                        borderColor: "transparent",
                        bgcolor: has ? ds.colors.primaryBg : "#f1f5f9",
                        color: has ? ds.colors.primary : "#0f172a",
                    },
                }}
                label="이동"
            />}
            <Menu open={Boolean(moveAnchor)} anchorEl={moveAnchor} onClose={() => setMoveAnchor(null)}>
                {/* 검색 모드에선 결과가 여러 폴더에 걸쳐 있으므로 '현재 폴더 제외' 를 적용하지 않는다.
                    blockedMoveTargets 는 서버가 <b>확실히</b> 거부할 대상만 담는다(현재는 받은 메일 → 보낸메일함).
                    남겨두면 눌러도 전건 실패하는 죽은 항목이 되기 때문. 애매한 건 담지 않는다 —
                    산정 근거는 MailPage 의 blockedMoveTargets 주석 참조. */}
                {FOLDERS.filter(f => f.key && (searchMode || f.key !== folder)
                                     && f.key !== "DRAFT" && f.key !== "SCHEDULED" && !f.virtual
                                     && !blockedMoveTargets.includes(f.key)).map(f => (
                    <MenuItem key={f.key} onClick={() => { onBulkMove(f.key); setMoveAnchor(null); }}>
                        <Box sx={{ mr: 1, display: "flex" }}>{ICON_MAP[f.key]}</Box>
                        <Typography sx={{ fontSize: 13 }}>{f.label}</Typography>
                    </MenuItem>
                ))}
                {customFolders && customFolders.length > 0 && <Divider />}
                {(customFolders || []).filter(cf => customKey(cf.id) !== folder).map(cf => (
                    <MenuItem key={cf.id} onClick={() => { onBulkMoveToFolder(cf.id); setMoveAnchor(null); }}>
                        <FolderRoundedIcon sx={{ fontSize: 18, mr: 1, color: cf.color || "#f59e0b" }} />
                        <Typography sx={{ fontSize: 13 }}>{cf.name}</Typography>
                    </MenuItem>
                ))}
            </Menu>

            {/*
              ⋮ 더보기 아이콘은 "선택 N건" placeholder 외엔 항목이 없어 의미 없는 미로였음.
              사용자 요청: 더보기 메뉴 없이 모든 액션을 인라인 노출. → 제거.
            */}

            <Box sx={{ flex: 1 }} />

            {/* 검색 중임을 툴바에서도 알린다 — 폴더 전용 버튼이 왜 사라졌는지 설명하는 문구 */}
            {searchMode && (
                <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mr: 0.8, whiteSpace: "nowrap" }}>
                    검색 결과(폴더 무관) — 폴더 전용 작업은 숨김
                </Typography>
            )}

            {/* 정렬 / 페이지 크기 */}
            <ActionButton variant="secondary"
                startIcon={<TuneRoundedIcon sx={{ fontSize: 14 }} />}
                onClick={onToggleSort}
                sx={{ fontSize: 12.5, color: "#64748b", borderColor: "transparent",
                    "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9", color: "#64748b" } }}
                label={sortDesc ? "최신순" : "오래된순"} />
            <Box sx={{ width: 96 }}>
                <ComboBox clearable={false}
                    options={PAGE_SIZE_OPTIONS.map(n => ({ value: n, label: `${n}개` }))}
                    value={pageSize}
                    onChange={v => onPageSize(Number(v))} />
            </Box>
        </Box>
    );
}

/* ──────────────────────────────────────────────────────────────────
 *  메일 목록 테이블 행
 *  ────────────────────────────────────────────────────────── */
/**
 * 컬럼 그리드 — MailRow 와 헤더가 동일하게 사용해 정렬 일치 보장.
 *  ☐ 28 / ★ 24 / ✉ 22 / 보낸이 180 / 제목 1fr / 날짜 170 / 용량 110
 *
 * 제목이 가변(1fr) — 화면 넓을수록 제목이 늘어남. 다만 날짜·용량을 넉넉히 두어 잘리지 않게
 * (긴 날짜 포맷·MB 단위 사이즈 표시 시 ellipsis 가 생기던 문제 해결).
 * (이전엔 "내용 280" 컬럼이 있었으나 preview 부정확성 / HTML·CSS 누출 문제로 컬럼 자체 제거.)
 */
/**
 * 제목 컬럼의 하한.
 *
 * <p>예전엔 제목이 그냥 {@code 1fr} 이었다. 다른 컬럼이 전부 고정 px 이라 컨테이너가 좁아지면 <b>제목만</b>
 * 줄어드는데, {@code 1fr} 에는 바닥이 없어서 0 까지 내려간다. 실측: 컨테이너 700px→제목 134px,
 * 600px→34px, 566px→13px(글자가 아예 안 보임). 넘쳤다는 신호는 어디에도 안 뜨고 제목만 조용히 증발한다.
 *
 * <p>160px 인 근거 — 본문 폰트(Pretendard 500 14px) 기준으로 셀 좌우 패딩(6.4×2)을 빼면
 * <b>한글 14자</b>가 들어간다. 첨부 아이콘(14.5px+gap)이 붙는 행은 12자. 영문은 22자(아이콘 시 19자).
 * 메일 제목은 앞 10자 남짓이면 어느 메일인지 구분되므로 12자를 최소 실사용선으로 잡았다.
 *
 * <p>하한을 주면 좁은 화면에서 표가 컨테이너를 넘긴다. 그게 목적이다 — 넘침이 가로 스크롤바로
 * <b>드러나서</b> 사용자가 오른쪽에 값이 더 있다는 걸 알게 된다(이전엔 제목이 사라질 뿐이었다).
 */
const SUBJECT_MIN_PX = 160;

const COLS = `28px 24px 22px 180px minmax(${SUBJECT_MIN_PX}px, 1fr) 170px 110px`;

/**
 * '배달' 컬럼을 하나 더 쓰는 폴더.
 *
 * <p>헤더와 행이 <b>같은 함수</b>로 컬럼을 잡아야 폴더를 바꿀 때 정렬이 어긋나지 않는다.
 */
const COLS_DELIVERY = `28px 24px 22px 180px minmax(${SUBJECT_MIN_PX}px, 1fr) 82px 170px 110px`;

/**
 * 사람 칸 대신 <b>유형</b>(수신/발신/임시보관/…) 칸을 쓰는 폴더 — 여러 종류가 <b>섞여 담기는</b> 곳들.
 *
 * <h4>왜 여기만 다른가</h4>
 * <p>한 종류만 담기는 폴더는 한 칸이면 되고, 그 칸의 제목도 폴더만 보고 정할 수 있다. 받은메일함은
 * 전부 받은 메일이라 상대방이 곧 보낸이고, 보낸메일함·임시보관함·예약함은 전부 내가 보낸 것이라
 * 상대방이 곧 받는이다.
 *
 * <p>휴지통·스팸함은 <b>섞인다</b>. 그런데 컬럼 제목은 폴더당 하나뿐이라, 지금까지 '보낸이' 라고
 * 써 놓고 내가 보낸 메일 행에는 수신자를 그렸다 — 제목이 행의 절반에 대해 거짓말을 했다
 * (값 자체는 맞았다. {@code toSummary} 유무로 행마다 옳게 골랐을 뿐 제목이 못 따라갔다).
 *
 * <p>여기서 실제로 필요한 판단은 "누구와" 가 아니라 <b>"이게 뭔가"</b> 다 — 복원할지, 스팸 해제할지,
 * 보내다 만 초안인지. 상대방은 행을 열면 바로 보인다.
 *
 * <h4>내 폴더(보관함 등)는 그대로 둔다</h4>
 * <p>거기도 섞이지만, 사용자가 직접 만들어 분류해 둔 곳이라 "누가 보냈나" 가 여전히 첫 단서다.
 * 휴지통·스팸함은 <b>처분</b>을 정하는 곳(복원할지, 스팸 해제할지)이라 유형이 먼저지만,
 * 내 폴더는 <b>찾는</b> 곳이다. 목적이 다르므로 같은 규칙을 밀어 넣지 않는다.
 */
const TYPE_COLUMN_FOLDERS = ["TRASH", "SPAM"];

/** 헤더·행이 <b>같은 판정</b>을 써야 컬럼 정렬이 어긋나지 않는다. */
const usesTypeColumn = (folder) => TYPE_COLUMN_FOLDERS.includes(folder);

/**
 * 사람 칸(180px)이 좁은 유형 칸(108px)으로 바뀐다. 휴지통·스팸은 배달 배지 대상이 아니라 둘이 겹칠 일은 없다.
 * <p>108px 인 근거: 고정 라벨 중 가장 긴 '발송 실패'(11px×5자 + 좌우 패딩)에 여유를 두고, 그 자리에
 * 사용자 폴더 이름이 들어올 수 있어 한글 5~6자까지는 안 잘리게 잡았다(더 길면 ellipsis + hover 툴팁).
 */
const COLS_TYPE = `28px 24px 22px 108px minmax(${SUBJECT_MIN_PX}px, 1fr) 170px 110px`;

/**
 * 메일 유형 배지 — 서버 {@code MailStatus} 를 그대로 쓴다.
 *
 * <p>방향(INBOUND/OUTBOUND)이 아니라 상태를 쓰는 이유: 시스템이 이미 구분해 둔 유형이 이쪽이다.
 * 방향만 보면 임시보관·예약·발송실패가 전부 '발신' 한 덩어리로 뭉개지는데, 휴지통에서
 * "보내다 만 초안" 과 "이미 나간 메일" 은 되살릴 때의 의미가 전혀 다르다.
 *
 * <p>색은 의미로 나눈다 — 이미 끝난 일(수신·발신)은 차분하게, 아직 안 끝난 일(임시보관·예약·발신중)은
 * 눈에 띄게, 실패는 경고색. 목록을 훑을 때 '손이 더 가야 하는 것' 이 먼저 보이게 하려는 것.
 */
const MAIL_TYPE_BADGE = {
    RECEIVED:  { label: "수신",      color: "#1e40af", bg: "#eff6ff" },
    SENT:      { label: "발신",      color: "#15803d", bg: "#f0fdf4" },
    DRAFT:     { label: "임시보관",  color: "#b45309", bg: "#fffbeb" },
    ARCHIVE:   { label: "보관함",    color: "#0f766e", bg: "#f0fdfa" },
    SCHEDULED: { label: "예약",      color: "#6d28d9", bg: "#f5f3ff" },
    SENDING:   { label: "발신 중",   color: "#475569", bg: "#f1f5f9" },
    FAILED:    { label: "발송 실패", color: "#b91c1c", bg: "#fef2f2" },
};

/** 원래 있던 폴더 → 배지. 휴지통·스팸의 {@code previousFolder} 를 그대로 옮긴다. */
const PREV_FOLDER_BADGE = {
    INBOX:     MAIL_TYPE_BADGE.RECEIVED,
    SENT:      MAIL_TYPE_BADGE.SENT,
    DRAFT:     MAIL_TYPE_BADGE.DRAFT,
    SCHEDULED: MAIL_TYPE_BADGE.SCHEDULED,
    ARCHIVE:   MAIL_TYPE_BADGE.ARCHIVE,
};

/**
 * 행 하나의 유형 배지 — <b>원래 있던 폴더</b>가 먼저다.
 *
 * <h4>왜 상태보다 폴더가 먼저인가</h4>
 * <p>휴지통에서 알고 싶은 건 "이걸 되살리면 어디로 가나" 이고, 그건 {@code previousFolder} 가 답한다.
 * 상태는 그걸 못 알려준다 — 보관함에 넣어 둔 메일도 상태는 여전히 RECEIVED 라, 상태만 보면
 * 받은메일함에서 지운 것과 구분이 안 된다. 게다가 화면의 '보관함' 은 고정 폴더가 아니라
 * <b>사용자 폴더</b>라 상태에는 흔적조차 없다.
 *
 * <h4>3단 폴백</h4>
 * <ol>
 *   <li>previousFolder — 휴지통·스팸으로 옮길 때만 기록된다(그 외 폴더에서는 늘 null)</li>
 *   <li>status — 스팸 자동분류처럼 previousFolder 가 없는 행, 그리고 발신중·발송실패</li>
 *   <li>direction — 이관·구버전 데이터에 status 가 빈 건이 실제로 있다</li>
 * </ol>
 * 셋 다 없으면 배지를 그리지 않는다(빈 배지가 더 헷갈린다).
 *
 * @param folderNameById 내 폴더 id → 이름. previousFolder 가 CUSTOM 일 때 그 폴더 이름을 배지로 쓴다
 *   ('보관함' 이 여기로 나온다). 그 사이 폴더가 지워졌으면 이름을 못 찾으므로 일반 '보관' 으로 떨어진다.
 */
function mailTypeBadge(mail, folderNameById) {
    const prev = mail?.previousFolder;
    if (prev === "CUSTOM") {
        const name = folderNameById?.get(mail.previousCustomFolderId);
        return { ...MAIL_TYPE_BADGE.ARCHIVE, label: name || "보관" };
    }
    if (PREV_FOLDER_BADGE[prev]) return PREV_FOLDER_BADGE[prev];

    const byStatus = MAIL_TYPE_BADGE[mail?.status];
    if (byStatus) return byStatus;
    if (mail?.direction === "INBOUND")  return MAIL_TYPE_BADGE.RECEIVED;
    if (mail?.direction === "OUTBOUND") return MAIL_TYPE_BADGE.SENT;
    return null;
}

const colsFor = (folder) =>
    DELIVERY_BADGE_FOLDERS.includes(folder) ? COLS_DELIVERY
  : usesTypeColumn(folder)                  ? COLS_TYPE
  : COLS;

/**
 * 목록이 절대 이보다 좁아지지 않는 폭 = 고정 컬럼 합 + 제목 하한 + 좌우 패딩(px:2 → 16×2).
 *
 * <p>왜 grid 에만 맡기지 않고 따로 계산하나: 헤더·행 각각은 스크롤 컨테이너 폭(=100%)에 맞춰 그려지므로,
 * 컬럼 합이 더 커도 컨테이너의 {@code scrollWidth} 는 늘지 않아 <b>가로 스크롤이 생기지 않는다.</b>
 * 스크롤 대상이 되려면 컨테이너 안쪽 래퍼가 실제로 그 폭을 차지해야 한다.
 * ({@code max-content} 를 쓰면 제목이 긴 메일 하나 때문에 표 전체가 수천 px 로 늘어나므로 쓰지 않는다.)
 */
const LIST_MIN_WIDTH          = 28 + 24 + 22 + 180 + SUBJECT_MIN_PX + 170 + 110 + 32;  // 726
const LIST_MIN_WIDTH_DELIVERY = LIST_MIN_WIDTH + 82;                                   // 808
// 사람 칸(180) → 유형 칸(108) 이라 휴지통·스팸은 오히려 72px 좁다.
const LIST_MIN_WIDTH_TYPE     = LIST_MIN_WIDTH - 72;                                   // 654
const listMinWidthFor = (folder) =>
    DELIVERY_BADGE_FOLDERS.includes(folder) ? LIST_MIN_WIDTH_DELIVERY
  : usesTypeColumn(folder)                  ? LIST_MIN_WIDTH_TYPE
  : LIST_MIN_WIDTH;

/**
 * TODO 배달상태 노출 범위 — 지금은 예약함·임시보관함에서만 보여준다.
 *
 * <p>원래는 보낸메일함(SENT)에서 켜 두었다. 반송·스팸신고를 발신자가 바로 알아채라고 만든 자리인데,
 * 실제로는 <b>"발송됨 — 발송 서버가 접수했습니다"</b> 에 머물러 있는 메일이 대부분이라 오히려
 * 혼란만 준다. 배달완료로 넘어가려면 SES 배달 이벤트(SNS→SQS→MailEventSqsPoller)가 들어와야 하는데,
 * 백업/아카이브에서 올린 메일에는 애초에 그 이벤트가 존재하지 않고, 신규 발송분도 SES configuration set
 * 이 Delivery 이벤트를 발행하도록 돼 있는지 확인이 안 된 상태다.
 *
 * <p><b>DRAFT 를 남겨 둔 이유</b> — 발송이 깨지면 {@code status=FAILED} 인 채로 임시보관함에 남는다
 * (MailSendService.recordSendFailure. 예약 발송 실패도 SCHEDULED→DRAFT 로 옮겨진다).
 * 여기서 배지를 끄면 "왜 안 나갔는지" 를 볼 자리가 아예 없어진다. 평범한 임시저장 메일은
 * {@code status=DRAFT} 라 배지 대상이 아니므로 아무것도 붙지 않는다 — 실패한 건만 눈에 띈다.
 *
 * <p><b>백엔드는 그대로 둔다</b> — 이벤트 수집·저장은 계속 돌고 있고, 반송률 관리(5% 넘으면 SES 계정
 * 정지)에 필요한 데이터다. 화면 노출만 잠갔다. 이벤트가 정상적으로 들어오는 게 확인되면
 * 여기에 "SENT" 를 다시 넣으면 된다 (MailDetail.jsx 의 DELIVERY_BADGE_FOLDERS 도 같이).
 */
const DELIVERY_BADGE_FOLDERS = ["SCHEDULED", "DRAFT"];

/**
 * 내 폴더에 쓸 수 있는 색 팔레트.
 *
 * <p>임의의 색을 고르게 하는 대신 12색으로 제한한 이유: 사이드바는 폴더 이름을 빠르게 훑는 곳이라
 * 대비가 낮은 색(연노랑·연회색)을 고르면 아이콘이 배경에 묻혀 오히려 못 찾게 된다. 여기 값들은
 * 모두 밝은 사이드바 배경(#fafbfc) 위에서 충분히 구분되는 채도로 골랐다.
 */
const FOLDER_COLORS = [
    "#f59e0b", // 주황 (기본)
    "#ef4444", // 빨강
    "#ec4899", // 분홍
    "#a855f7", // 보라
    "#6366f1", // 남보라
    "#2563eb", // 파랑
    "#0ea5e9", // 하늘
    "#14b8a6", // 청록
    "#22c55e", // 초록
    "#84cc16", // 연두
    "#78716c", // 회갈
    "#475569", // 진회색
];
const DEFAULT_FOLDER_COLOR = FOLDER_COLORS[0];

/** 셀 공통 스타일 — 기본 가운데 정렬, align 인자로 좌/우 정렬 가능 */
const cellSx = (align = "center") => ({
    display: "flex",
    alignItems: "center",
    justifyContent:
        align === "left"  ? "flex-start" :
        align === "right" ? "flex-end"   : "center",
    minWidth: 0,
    px: 0.8,
    height: "100%",
});

/* ── 컬럼 헤더 — 테이블 라벨 행 ─ */
/**
 * 첫 칸의 체크박스로 <b>현재 페이지 전체</b>를 선택/해제한다.
 *
 * <p>전체 선택은 원래 상단 툴바에만 있었다. 하지만 표를 보다가 "다 고르고 싶다" 는 생각이 드는 지점은
 * 목록 바로 위 헤더 행이지 한 칸 더 위의 툴바가 아니다 — 다른 표들이 모두 헤더에 두는 이유이기도 하다.
 * 툴바 쪽 체크박스는 그대로 두어 기존 사용 습관을 깨지 않는다(같은 상태를 공유하므로 둘은 항상 일치한다).
 */
/**
 * 컬럼 제목이 '받는이' 가 되는 폴더 — 내가 보낸(보내려던) 메일만 담기는 위치들.
 * <p>SCHEDULED(예약함)가 빠져 있어 예약함만 제목이 '보낸이' 로 나왔다. 값 자체는 행별로
 * {@code toSummary} 유무가 결정하므로(MailRow) 여기는 제목 표기만 담당한다.
 */
const SENDER_SIDE_FOLDERS = ["SENT", "DRAFT", "SCHEDULED", "SELF"];

/**
 * 읽음/안읽음 구분을 화면에 드러내는 폴더 — ✉ 아이콘 컬럼 + 안 읽은 행 굵게.
 *
 * <p>보낸·임시·내게쓰기·스팸·휴지통에서는 의미가 없다(대부분 자기가 쓴 메일이라 늘 '읽음' 이다).
 * '안 읽은 메일'(UNREAD)이 여기 들어있는 이유: 열어서 읽음이 된 행이 <b>그 자리에서 흐려져야</b>
 * 방금 뭘 처리했는지 보인다. 목록에서 즉시 지우지는 않는다 — 커서 밑의 행이 사라지면
 * 바로 아래 메일을 잘못 누른다. 다음 새로고침에서 빠진다.
 */
const READ_STATE_FOLDERS = ["INBOX", "UNREAD"];

function MailListHeader({ folder, allChecked, indeterminate, onToggleAll }) {
    const typeCol  = usesTypeColumn(folder);
    const showFrom = !SENDER_SIDE_FOLDERS.includes(folder);
    /** 하나라도 선택돼 있는가 — 누르면 비워지는 상태(전체든 일부든). onToggleAll 주석 참고. */
    const anySelected = allChecked || indeterminate;
    return (
        <Box sx={{
            display: "grid", gridTemplateColumns: colsFor(folder), alignItems: "stretch",
            px: 2,           // 툴바와 동일한 좌우 패딩 → 체크박스 컬럼 위치 일치
            py: 0.75,
            // 스크롤 상자 '안' 에 있으면서 세로로만 고정 — top 만 주고 left 는 주지 않는 게 핵심이다.
            // 예전엔 헤더를 상자 밖에 둬서 세로 고정은 됐지만 가로 스크롤을 따라가지 못했다.
            // 그 결과 열을 오른쪽으로 밀면 라벨과 값이 어긋나 어느 값이 어느 컬럼인지 알 수 없었다.
            // sticky 는 가로로는 콘텐츠와 같이 움직이므로 정렬이 유지된다.
            position: "sticky", top: 0, zIndex: 2,
            // 헤더 색 — Naver 메일 톤. 너무 진한 회색 (#f8fafc, slate-50) 은 사용자가 "회색이 짙다" 보고.
            // 한 단계 연한 #fafbfc 로 변경 — 거의 흰색에 가까운 미세한 톤만 남김.
            bgcolor: "#fafbfc", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0",
            color: "#475569", fontSize: 12.5, fontWeight: 700, letterSpacing: 0.2,
        }}>
            <Box sx={cellSx()}>
                {onToggleAll && (
                    /* 일부 선택('–')일 때도 누르면 비워지므로 안내 문구를 그렇게 맞춘다 —
                       '전체 선택' 이라고 적어두면 해제하려는 사람이 누르길 망설인다. */
                    <Tooltip title={anySelected ? "선택 해제" : "이 페이지 전체 선택"}>
                        <Checkbox
                            size="small"
                            checked={Boolean(allChecked)}
                            indeterminate={Boolean(indeterminate)}
                            onChange={onToggleAll}
                            sx={{ p: 0 }}
                        />
                    </Tooltip>
                )}
            </Box>
            <Box sx={cellSx()} />
            <Box sx={cellSx()} />
            {/* 유형 칸만 가운데 정렬 — 값이 폭이 제각각인 배지 하나뿐이라 왼쪽에 붙이면
                라벨과 칩이 어긋나 보인다(날짜·용량 칸과 같은 취급). */}
            {typeCol
                ? <Box sx={cellSx()}>유형</Box>
                : <Box sx={cellSx("left")}>{showFrom ? "보낸이" : "받는이"}</Box>}
            <Box sx={cellSx("left")}>제목</Box>
            {DELIVERY_BADGE_FOLDERS.includes(folder) && <Box sx={cellSx()}>배달</Box>}
            <Box sx={cellSx()}>날짜</Box>
            <Box sx={cellSx()}>용량</Box>
        </Box>
    );
}

function MailRow({ mail, checked, onCheck, onOpen, onToggleStar, folder, folderNameById }) {
    const isUnread = !mail.isRead;
    const fromName = mail.fromName || "";
    const fromAddr = mail.fromAddr || "";

    // 보낸메일함·임시보관함·예약함·내게쓰기는 '받는이' 를 보여준다.
    //
    // [예전 버그] 컬럼 제목만 '받는이' 로 바뀌고 값은 폴더와 무관하게 항상 fromName 을 그렸다.
    //   보낸 메일의 발신자는 정의상 항상 본인이라 "받는이: 내 이름" 이 나왔다(현장 신고).
    //
    // 폴더를 여기서 다시 판정하지 않는다 — 서버가 '발신자 측 상태행' 인 행에만 toSummary 를 채워 준다
    // (MailListItemDto.toSummary / MailQueryService.loadToSummaries 주석). 프론트의 폴더 값으로는
    // 휴지통·보관함으로 옮긴 보낸 메일을 구분하지 못한다.
    const showTo   = Boolean(mail.toSummary);
    const mainText = showTo ? mail.toSummary : (fromName || fromAddr);
    const subText  = showTo ? "" : (fromName && fromAddr ? fromAddr : "");

    // 섞인 폴더(휴지통·스팸함·내 폴더)는 사람 대신 유형 배지를 그린다 —
    // 위 mainText/subText 는 그 폴더들에서 쓰이지 않는다.
    const typeCol   = usesTypeColumn(folder);
    const typeBadge = typeCol ? mailTypeBadge(mail, folderNameById) : null;

    // 읽음/안읽음 구분은 받은메일함·안읽은메일에서만 의미 있음 (READ_STATE_FOLDERS 주석 참고).
    // 보낸·임시·내게쓰기·스팸·휴지통·보관함은 글자 흐리게 처리하지 않는다.
    const useReadDim = READ_STATE_FOLDERS.includes(folder);
    const dimmed     = useReadDim && !isUnread;

    // 색상 톤 — 읽음 (dimmed) 행은 제목/발신자 모두 한 톤 흐리게.
    const titleColor   = dimmed ? "#64748b" : "#0f172a";
    const fromColor    = dimmed ? "#64748b" : "#0f172a";
    const subFromColor = dimmed ? "#94a3b8" : "#64748b";

    return (
        <Box
            onClick={onOpen}
            sx={{
                display: "grid",
                gridTemplateColumns: colsFor(folder),
                alignItems: "stretch",
                px: 2,           // 툴바·헤더와 동일 좌우 패딩 → 체크박스 컬럼 위치 일치
                cursor: "pointer",
                bgcolor: checked ? "#eff6ff" : "#fff",
                borderBottom: "1px solid #f1f5f9",
                minHeight: 48,
                // 쉬프트 클릭으로 범위를 잡을 때 브라우저가 행들 사이 글자를 파랗게 긁는다.
                // 선택은 체크박스가 하는 일이라, 드래그 선택 자체를 끈다.
                userSelect: "none",
                // hover 톤 — 헤더와 일관. 너무 진한 회색이 "잿빛" 으로 보이지 않도록.
                "&:hover": { bgcolor: checked ? "#dbeafe" : "#fafbfc" },
            }}>
            {/* 체크박스 — 툴바와 동일 padding 으로 시각 정렬 */}
            <Box onClick={(e) => e.stopPropagation()} sx={cellSx()}>
                <Checkbox
                    size="small" checked={checked}
                    /* 쉬프트 여부는 nativeEvent 에서 읽는다 — React 의 checkbox onChange 는 내부적으로
                       click 이벤트라 shiftKey 가 실려 있다. 키보드(스페이스)로 토글하면 false 라
                       단일 토글로 떨어지는데, 그게 맞는 동작이다. */
                    onChange={(e) => onCheck(mail.id, e.target.checked, e.nativeEvent?.shiftKey)}
                    sx={{ p: 0.5, color: "#cbd5e1", "&.Mui-checked": { color: ds.colors.primary } }}
                />
            </Box>
            {/* 별표 */}
            <Box onClick={(e) => e.stopPropagation()} sx={cellSx()}>
                <IconButton size="small" onClick={() => onToggleStar(mail.id)} sx={{ p: 0.2 }}>
                    {mail.isStarred
                        ? <StarRoundedIcon       sx={{ fontSize: 17, color: "#f59e0b" }} />
                        : <StarBorderRoundedIcon sx={{ fontSize: 17, color: "#cbd5e1" }} />}
                </IconButton>
            </Box>
            {/* 읽음/안읽음 — 받은메일함·안읽은메일에서만 표시 (다른 폴더는 빈 칸) */}
            <Box sx={cellSx()}>
                {useReadDim && (
                    isUnread
                        ? <MarkEmailUnreadRoundedIcon sx={{ fontSize: 16, color: ds.colors.primary }} />
                        : <MarkEmailReadRoundedIcon   sx={{ fontSize: 16, color: "#cbd5e1" }} />
                )}
            </Box>
            {/* 휴지통은 사람 대신 유형(수신/발신/임시보관/…) — TYPE_COLUMN_FOLDERS 주석 참고.
                그 밖의 폴더는 보낸이(받은 메일) 또는 받는이(보낸 메일)를 좌측 정렬로, 이름 위·이메일 아래. */}
            {typeCol ? (
                <Box sx={cellSx()}>
                    {typeBadge && (
                        // 사용자 폴더 이름이 그대로 들어올 수 있어 잘림 처리가 필요하다
                        // ('발송 실패' 같은 고정 라벨은 칸 안에 들어가지만 폴더 이름은 길이를 알 수 없다).
                        // title 을 함께 달아 잘려도 hover 로 전체 이름을 확인할 수 있게 한다.
                        <Box component="span" title={typeBadge.label} sx={{
                            fontSize: 11, fontWeight: 700, lineHeight: 1.6,
                            color: typeBadge.color, bgcolor: typeBadge.bg,
                            borderRadius: "4px", px: 0.8, py: 0.1, whiteSpace: "nowrap",
                            display: "inline-block", maxWidth: "100%",
                            overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                            {typeBadge.label}
                        </Box>
                    )}
                </Box>
            ) : (
                <Box sx={cellSx("left")}>
                    <Box sx={{ minWidth: 0, textAlign: "left", width: "100%" }}>
                        <Typography sx={{
                            fontSize: 13.5,
                            fontWeight: useReadDim && isUnread ? 700 : 500,
                            color: fromColor,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3,
                        }}>
                            {mainText || (showTo ? "(받는이 없음)" : "(발신자 없음)")}
                        </Typography>
                        {subText && (
                            <Typography sx={{
                                fontSize: 11, color: subFromColor, lineHeight: 1.3,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {subText}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            {/* 제목 (+ 첨부 아이콘) — 좌측 정렬 */}
            <Box sx={cellSx("left")}>
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 0.5,
                    minWidth: 0, width: "100%", justifyContent: "flex-start",
                }}>
                    <Typography sx={{
                        fontSize: 14,
                        fontWeight: useReadDim && isUnread ? 700 : 500,
                        color: titleColor,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        textAlign: "left",
                    }}>
                        {mail.subject || "(제목 없음)"}
                    </Typography>
                    {mail.hasAttachments && (
                        <AttachFileRoundedIcon sx={{ fontSize: 14.5, color: "#94a3b8", transform: "rotate(45deg)", flexShrink: 0 }} />
                    )}
                </Box>
            </Box>
            {/* 배달 상태 — 지금은 예약함·임시보관함에서만. 범위와 이유는 DELIVERY_BADGE_FOLDERS 주석 참고. */}
            {DELIVERY_BADGE_FOLDERS.includes(folder) && (
                <Box onClick={(e) => e.stopPropagation()} sx={cellSx()}>
                    <DeliveryStatusBadge mail={mail} />
                </Box>
            )}
            {/* 날짜 */}
            <Box sx={cellSx()}>
                <Typography sx={{ fontSize: 12, color: dimmed ? "#94a3b8" : "#64748b", textAlign: "center" }}>
                    {formatDate(mail.receivedAt)}
                </Typography>
            </Box>
            {/* 용량 */}
            <Box sx={cellSx()}>
                <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
                    {formatSize(mail.sizeBytes)}
                </Typography>
            </Box>
        </Box>
    );
}

/**
 * 카운트 응답 언랩 — 사이드바 뱃지용 {폴더키: 개수} 로 정규화한다.
 *
 * <p>같은 "폴더별 카운트"인데 응답 모양이 두 가지다.
 *   - /unread-counts : MailUnreadCountDto → {@code {counts:{INBOX:3,...}}} (한 겹 감싸짐)
 *   - /total-counts  : Map 직접 반환      → {@code {INBOX:12,...}}
 * axios 인터셉터가 이미 response.data 를 벗겨주지만, 벗기지 않은 응답이 섞여도 안전하도록
 * data → counts 순으로 방어적으로 벗긴다. 폴더 키에는 "data"/"counts" 가 없어 충돌하지 않는다.
 */
const unwrapCounts = (res) => {
    const d = res?.data ?? res ?? {};
    return d?.counts ?? d ?? {};
};

/* ──────────────────────────────────────────────────────────────────
 *  메인
 *  ────────────────────────────────────────────────────────────── */
export default function MailPage() {
    const [view, setView]             = useState("mail");
    /** 연락처 화면을 열 때 처음 보여줄 탭 — 사이드바의 '내 연락처'/'공용 연락처' 가 이 값으로 갈린다. */
    const [contactScope, setContactScope] = useState("PERSONAL");
    const [folder, setFolder]         = useState("INBOX");
    // 예약함 내부 탭: 0 = 예약 메일, 1 = 단체 발송
    const [scheduledTab, setScheduledTab] = useState(0);
    const [mails, setMails]           = useState([]);
    const [loading, setLoading]       = useState(false);
    const [unreadCounts, setUnreadCounts] = useState({});
    const [query, setQuery]           = useState("");
    /** 지금 화면의 목록이 '검색 결과'인지 — 검색은 폴더 무관이라 폴더 전용 액션을 가리는 근거가 된다. */
    const [searchActive, setSearchActive] = useState(false);

    /* 사용자 정의 폴더 */
    const [customFolders, setCustomFolders] = useState([]);
    const [customCounts, setCustomCounts]   = useState({ total: {}, unread: {} });
    /**
     * 내 폴더 id → 이름. 휴지통·스팸의 '원래 있던 폴더' 배지가 쓴다({@code mailTypeBadge}).
     * 서버가 이름을 안 붙이고 id 만 주는 이유는 MailListItemDto.previousCustomFolderId 주석 참고 —
     * 사이드바를 그리려고 어차피 갖고 있는 목록이라 여기서 한 번만 Map 으로 만든다.
     */
    const folderNameById = useMemo(
        () => new Map((customFolders || []).map(f => [f.id, f.name])),
        [customFolders]);
    const [folderDialog, setFolderDialog]   = useState(null);   // { mode:'create'|'rename', id? } | null
    const [folderNameInput, setFolderNameInput] = useState("");
    const [folderSaving, setFolderSaving]   = useState(false);

    /* 페이징 */
    const [page, setPage]             = useState(0);
    const [pageSize, setPageSize]     = useState(100);
    // 페이지 전체 건수는 화면에 쓰지 않는다(사이드바 개수는 totalCounts 가 따로 담당) —
    // 값을 읽는 곳이 없어 상태를 두지 않고, 목록 응답에서도 totalPages 만 쓴다.
    const [totalPages, setTotalPages] = useState(1);
    const [sortDesc, setSortDesc]     = useState(true); // 백엔드 기본 desc

    /* 일괄 선택 */
    const [selectedIds, setSelectedIds] = useState([]);
    /**
     * 범위 선택 기준점 — 마지막으로 <b>직접</b> 클릭한 행 id. 쉬프트 클릭이 "여기부터 저기까지" 를
     * 잡을 때 한쪽 끝으로 쓴다 (handleCheck 참고). state 가 아니라 ref 인 이유: 이 값이 바뀌어도
     * 다시 그릴 것이 없다. 목록이 새로 뜨면 loadList 가 폐기한다.
     */
    const anchorRef = useRef(null);

    /* 상세 모달 */
    const [detail, setDetail]               = useState(null);
    const [detailOpen, setDetailOpen]       = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);  // 상세 GET 중 스피너 표시용

    /* 작성 */
    const [composeOpen, setComposeOpen]     = useState(false);
    const [composeInitial, setComposeInitial] = useState(null);

    /* 사이드바 딥링크: ?folder=INBOX|SENT 로 폴더 이동, ?compose=1 로 메일 작성 모달 열기,
       ?contacts=PERSONAL|COMPANY|PUBLIC 로 연락처 화면의 해당 탭, ?groups=1 로 수신 그룹 화면.
       (TEAM 은 2026-08-08 폐지 — 목록에서 빠졌으니 옛 링크는 기본 탭으로 열린다.)
       수신 그룹이 ?contacts= 가 아닌 이유: 연락처 탭이 아니라 별도 뷰다. 탭 값처럼 보이게 두면
       "그럼 연락처에 그 탭이 있나" 를 매번 찾게 된다.
       compose 는 열자마자 파라미터를 제거해 새로고침·재실행 시 재오픈되지 않게 한다.
       (contacts·groups·folder 는 남겨둔다 — 화면 상태라 새로고침해도 같은 곳이 열려야 한다.) */
    const location = useLocation();
    const history  = useHistory();
    useEffect(() => {
        const sp = new URLSearchParams(location.search);
        const f = sp.get("folder");
        if (f && FOLDERS.some(x => x.key === f)) { setFolder(f); setView("mail"); }
        const c = sp.get("contacts");
        if (c && ["PERSONAL", "COMPANY", "PUBLIC"].includes(c)) {
            setContactScope(c);
            setView("contacts");
        }
        if (sp.get("groups") === "1") setView("groups");
        if (sp.get("compose") === "1") {
            setComposeInitial(null);
            setComposeOpen(true);
            sp.delete("compose");
            history.replace({ pathname: location.pathname, search: sp.toString() ? `?${sp}` : "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    /* 내 정보 (내게쓰기용) + 권한 (사이드바 "AI 프롬프트(관리자)" 노출 판단) */
    const [me, setMe] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    /* NAS 대량 이관 메뉴 노출 — 서버가 '관리자 + 담당자 계정 + NAS 활성' 을 한 번에 판단한다.
       화면에서 계정명을 다시 비교하면 서버 규칙과 갈라져, 담당자인데 메뉴가 안 보이거나
       반대로 보이는데 403 이 나는 상황이 생긴다. 판단은 한 곳에만 둔다. */
    const [canArchive, setCanArchive] = useState(false);
    /* 타인 메일 열람 메뉴(전체 메일·직원 메일함·미배정 메일) 노출 — 관리자 권한만으로는 부족하고
       지정 담당자 계정이어야 한다. 판단은 canArchive 와 같은 이유로 전적으로 서버에 맡긴다:
       여기서 계정명을 다시 비교하면 규칙이 두 곳이 되어 언젠가 서버와 갈라진다. */
    const [canViewOthersMail, setCanViewOthersMail] = useState(false);
    useEffect(() => {
        apiClient.get(`${API_BASE}/employees/me`).then(setMe).catch(() => {});
        apiClient.get(`${API_BASE}/employees/me/permissions`).then((perms) => {
            const codes = (perms || []).map(p =>
                typeof p === "string" ? p : (p?.code || p?.permissionCode || p?.name || ""));
            setIsAdmin(codes.includes("GLOBAL_ADMIN"));
        }).catch(() => setIsAdmin(false));
        apiClient.get(`${API_BASE}/mail-archive/available`)
            .then(r => setCanArchive(!!r?.available))
            .catch(() => setCanArchive(false));
        apiClient.get(`${API_BASE}/mail/admin/capabilities`)
            .then(r => setCanViewOthersMail(!!r?.canViewOthersMail))
            .catch(() => setCanViewOthersMail(false));
    }, []);

    const folderMeta = useMemo(() => {
        if (isCustomKey(folder)) {
            const cf = customFolders.find(c => c.id === customIdOf(folder));
            return { key: folder, label: cf?.name || "폴더",
                     desc: "내가 만든 폴더입니다. 여기에 보관한 메일을 볼 수 있습니다." };
        }
        return FOLDERS.find(f => f.key === folder) || FOLDERS[0];
    }, [folder, customFolders]);
    const headerIcon = isCustomKey(folder)
        ? <FolderRoundedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
        : ICON_MAP[folder];

    /**
     * 목록 로드 — 폴더별 / 검색어 입력 시 전체 메일 검색 / SELF 가상 폴더 별도 endpoint
     *
     * 검색 모드 진입 조건
     *   query 가 비어있지 않으면 search endpoint 호출 (폴더 무관, 휴지통 제외, 본인 메일만).
     *   비어있으면 일반 listByFolder.
     *
     * <p>검색 결과는 여러 폴더의 메일이 섞여 있으므로 지금 화면에 뜬 목록이 검색 결과인지를
     * searchActive 로 남긴다 — 툴바가 폴더 전용 액션(스팸 해제·복원 등)을 숨기는 근거다.
     * (query 대신 이 값을 쓰는 이유: 입력 디바운스 350ms 동안은 아직 폴더 목록이 떠 있다.)
     */
    const loadList = async () => {
        setLoading(true);
        try {
            const q = query.trim();
            setSearchActive(Boolean(q));
            const res = q
                ? await mailApi.search(q, page, pageSize)              // 검색 모드 — 전체 메일
                : (folder === "SELF"      ? await mailApi.listSelfMails(page, pageSize)
                :  folder === "UNREAD"    ? await mailApi.listUnread(page, pageSize)
                :  folder === "STARRED"   ? await mailApi.listStarred(page, pageSize)
                :  folder === "ALL"       ? await mailApi.listAll(page, pageSize)
                :  isCustomKey(folder)    ? await mailApi.listCustomFolder(customIdOf(folder), page, pageSize)
                :  await mailApi.listByFolder(folder, page, pageSize, starredOnly));
            const data = res?.data ?? res ?? {};
            const content = Array.isArray(data) ? data : (data.content || []);
            setMails(content);
            setTotalPages(data.totalPages ?? 1);
        } catch (e) {
            setMails([]); setTotalPages(1);
        } finally {
            setLoading(false);
            setSelectedIds([]);
            // 기준점도 같이 버린다 — 남겨두면 새 목록의 엉뚱한 행이 범위의 한쪽 끝이 된다.
            anchorRef.current = null;
        }
    };

    /**
     * 안 읽음 카운트 — 사이드바 뱃지.
     *
     * <p>백엔드 MailUnreadCountDto 는 폴더맵을 {@code {counts:{INBOX:3,...}}} 로 한 겹 감싸는데
     * 예전엔 최상위에서 바로 폴더 키를 읽어(unreadCounts.INBOX) 항상 undefined → 뱃지가 늘 0 이었다.
     * 이제 unwrapCounts 로 counts 한 겹을 벗겨 {@code {INBOX:3,...}} 형태로 저장한다.
     *
     * <p>같은 숫자를 좌측 전역 사이드바에도 흘려보낸다("mail:unread"). 사이드바가 스스로 다시
     * 조회하면 요청이 두 배가 되고 두 화면의 숫자가 잠깐씩 어긋난다 — 여기서 한 번 받아 나눠 쓴다.
     * 이 함수는 새로고침·읽음처리·삭제·이동 뒤에 모두 불리므로, 갱신 지점을 따로 챙길 필요가 없다.
     */
    const loadUnread = useCallback(async () => {
        try {
            const res = await mailApi.unreadCounts();
            const counts = unwrapCounts(res);
            setUnreadCounts(counts);
            window.dispatchEvent(new CustomEvent("mail:unread", { detail: counts }));
        } catch { setUnreadCounts({}); }
    }, []);

    /** 폴더별 전체 메일 카운트 — 사이드바 폴더명 옆 표시용 (백엔드가 Map 을 그대로 반환) */
    const [totalCounts, setTotalCounts] = useState({});
    const loadTotalCounts = useCallback(async () => {
        try {
            const res = await mailApi.totalCounts();
            setTotalCounts(unwrapCounts(res));
        } catch { setTotalCounts({}); }
    }, []);

    /**
     * 폴더 안에서 «중요만 보기».
     *
     * <p>사이드바 «중요»(가상 폴더)와 쓰임이 다르다. 그쪽은 폴더를 가로질러 모으고, 이건
     * 보고 있던 폴더의 경계를 유지한 채 좁힌다 — "받은메일함에서 중요한 것만" 이 실제 쓰임이다.
     *
     * <p>폴더를 옮기면 끈다. 켠 채로 옮기면 «비어 있는 폴더» 를 보고 메일이 없다고 오해한다.
     */
    const [starredOnly, setStarredOnly] = useState(false);

    /**
     * 중요(별표) 메일 수 — 사이드바 «중요» 옆 숫자.
     *
     * <p>total-counts 에 끼워 넣지 않는다. 그쪽은 «폴더에 든 개수» 라 진짜 폴더만 담기고,
     * 중요는 폴더를 가로지르는 묶음이라 합계가 폴더 합과 맞지 않는다 — 같은 맵에 섞으면
     * "폴더 숫자를 다 더해도 전체가 안 맞는다" 가 된다.
     */
    const [starredCount, setStarredCount] = useState(0);
    const loadStarredCount = useCallback(async () => {
        try {
            const res = await mailApi.starredCount();
            setStarredCount(Number(res?.data ?? res ?? 0) || 0);
        } catch { setStarredCount(0); }
    }, []);

    /** 내 폴더 목록 로드 */
    const loadCustomFolders = useCallback(async () => {
        try {
            const res = await mailApi.listFolders();
            let list = Array.isArray(res?.data ?? res) ? (res?.data ?? res) : [];
            if (list.length === 0) {
                // 고정 '보관함' 대체 — 내 폴더 기본으로 '보관함'을 자동 생성
                try {
                    await mailApi.createFolder({ name: "보관함", color: "#0ea5e9" });
                    const r2 = await mailApi.listFolders();
                    list = Array.isArray(r2?.data ?? r2) ? (r2?.data ?? r2) : [];
                } catch { /* 이미 있거나 실패 — 무시 */ }
            }
            setCustomFolders(list);
        } catch { setCustomFolders([]); }
    }, []);

    /** 내 폴더별 카운트 로드 */
    const loadCustomCounts = useCallback(async () => {
        try {
            const res = await mailApi.customCounts();
            const d = res?.data ?? res ?? {};
            setCustomCounts({ total: d.total || {}, unread: d.unread || {} });
        } catch { setCustomCounts({ total: {}, unread: {} }); }
    }, []);

    /** 이동/삭제 등으로 개수가 바뀔 때 사이드바 뱃지 일괄 갱신 */
    const refreshCounts = useCallback(() => {
        loadUnread(); loadTotalCounts(); loadCustomCounts(); loadStarredCount();
    }, [loadUnread, loadTotalCounts, loadCustomCounts, loadStarredCount]);

    useEffect(() => { loadList();
        // eslint-disable-next-line
    }, [folder, page, pageSize, starredOnly]);

    /* 폴더를 옮기면 «중요만 보기» 를 끈다 — 켠 채로 옮기면 빈 목록을 보고 메일이 없다고 오해한다. */
    useEffect(() => { setStarredOnly(false); }, [folder]);
    useEffect(() => {
        loadUnread(); loadTotalCounts(); loadCustomFolders(); loadCustomCounts(); loadStarredCount();
    }, [loadUnread, loadTotalCounts, loadCustomFolders, loadCustomCounts, loadStarredCount]);

    /**
     * 검색어 입력 디바운스 — 사용자가 타이핑 중일 때 매 키마다 호출 안 하고 350ms 후 1번만 호출.
     * 빈 문자열로 바뀌면 즉시 일반 폴더 목록으로 복귀 (loadList 가 query 비어있음을 감지해 listByFolder).
     *
     * <p>첫 마운트 시 query="" 변경이 감지되어 위 [folder, page, pageSize] useEffect 와 함께
     * 두 번 fetch 가 발생하던 문제 → skipFirstQuery ref 로 첫 마운트는 skip.
     */
    const skipFirstQuery = useRef(true);
    useEffect(() => {
        if (skipFirstQuery.current) {
            skipFirstQuery.current = false;
            return;
        }
        const t = setTimeout(() => {
            setPage(0);
            loadList();
        }, query.trim() ? 350 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [query]);

    // 검색은 백엔드 endpoint 가 처리 — 클라이언트 측 추가 필터 불필요.
    // visibleMails 는 그대로 두되 mails 자체가 이미 필터 결과.
    const visibleMails = mails;
    // 표시 순서 메모이제이션 — sortDesc 가 false 일 때 매 렌더마다 reverse 배열 생성 방지.
    const orderedMails = useMemo(
        () => sortDesc ? visibleMails : [...visibleMails].reverse(),
        [visibleMails, sortDesc],
    );

    /**
     * 행 클릭 — 메일 상세 모달 열기.
     *
     * <h4>로딩 UX</h4>
     * <p>이전엔 getDetail 응답을 await 한 후에 모달을 열었음 → 1~2초 동안 화면에 아무 변화 없어
     * 사용자가 클릭이 먹혔는지 모름. 변경: 모달은 즉시 open + LoadingDots 표시,
     * getDetail 응답 도착하면 본문으로 교체.
     */
    /** 관리자 열람 — 본인 user_state 없이도 상세 조회(감사 READ_OTHER 자동 기록). 응답 DTO 는 일반 상세와 동일해 같은 모달을 재사용. */
    const handleOpenAsAdmin = async (mailId) => {
        setDetail(null);
        setDetailLoading(true);
        setDetailOpen(true);
        try {
            const res = await mailApi.getMailAsAdmin(mailId);
            setDetail(res?.data ?? res);
        } catch (e) {
            notify(e?.response?.data?.message || "메일을 불러오지 못했습니다", "error");
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleOpen = async (mail) => {
        // ── 성능 진단 ──────────────────────────────────────────────
        // 메일 클릭이 느릴 때 어디서 시간이 가는지 콘솔에 단계별로 찍는다.
        //   modalOpen     : 빈 모달 + 스피너 (즉시 보여야 함)
        //   apiCall       : /api/mail/{id} 응답 도착까지 — 백엔드 [getDetail timing] 로그와 비교
        //                   (network + 백엔드 stateLookup + msgLookup + assemble)
        //   setState      : setDetail / setDetailLoading 호출 시간
        //   bodyHtmlLen   : 응답 본문 길이 — sanitize/렌더 비용의 입력 크기
        //   total         : 클릭 → 모달이 본문으로 교체되기까지 wall time
        // 백엔드 로그 [MailQuery/getDetail timing] + [MailQuery/assemble timing] 와 비교하면
        // 네트워크 / 직렬화 / 프론트 렌더 어디서 병목인지 분리 가능.
        const t0 = performance.now();
        setDetail(null);
        setDetailLoading(true);
        setDetailOpen(true);
        const t1 = performance.now();

        try {
            const res = await mailApi.getDetail(mail.id);
            const t2 = performance.now();
            const detailData = res?.data ?? res ?? mail;
            setDetail(detailData);
            const t3 = performance.now();
            const htmlLen = (detailData && detailData.bodyHtml) ? detailData.bodyHtml.length : 0;
            console.log(
                `[mail open timing] id=${mail.id} ` +
                `modalOpen=${(t1 - t0).toFixed(1)}ms ` +
                `apiCall=${(t2 - t1).toFixed(1)}ms ` +
                `setState=${(t3 - t2).toFixed(1)}ms ` +
                `total=${(t3 - t0).toFixed(1)}ms bodyHtmlLen=${htmlLen}`
            );
        } catch {
            setDetail(mail);
        } finally {
            setDetailLoading(false);
        }

        if (!mail.isRead) {
            try { await mailApi.markRead(mail.id); } catch { /* noop */ }
            setMails(ms => ms.map(m => m.id === mail.id ? { ...m, isRead: true } : m));
            loadUnread();
        }
    };

    const handleToggleStar = async (mailId) => {
        try {
            await mailApi.toggleStar(mailId);
            setMails(ms => ms.map(m => m.id === mailId ? { ...m, isStarred: !m.isStarred } : m));
        } catch { /* noop */ }
    };

    /* 일괄 선택 */
    const allChecked    = visibleMails.length > 0 && selectedIds.length === visibleMails.length;
    const indeterminate = selectedIds.length > 0 && selectedIds.length < visibleMails.length;

    /**
     * 이동 메뉴에서 <b>숨길</b> 대상 폴더 — 서버 이동 가드(MailQueryService.move)와 같은 판정을 미리 한다.
     *
     * <p><b>판정 기준은 "메일의 direction" 이 아니라 "이 목록 행(상태행)이 발신자 측인가" 다.</b>
     * 하나의 mail_message 에 발신자 측 상태행과 수신자 측 상태행이 <b>동시에</b> 존재할 수 있기 때문이다 —
     * 사내 A→B 메일은 From 이 사내 직원이라 direction=OUTBOUND 지만, B 에게는 정상적인 수신자 INBOX
     * 상태행이 생긴다(routeToEmployees / ensureInboxState). 그래서 direction=OUTBOUND 라는 이유만으로
     * INBOX 를 메뉴에서 지우면 B 는 자기 받은 메일을 받은 메일함으로 되돌릴 <b>우회 경로조차 없어진다.</b>
     *
     * <p>목록 DTO(MailListItemDto)가 주는 필드는 id/direction/status/folder(내 상태행 폴더) 정도이고,
     * "이 상태행이 발신자 측" 임을 <b>확정</b>할 수 있는 필드는 없다. folder==="SENT" 도 근거가 못 된다 —
     * 예전 가드는 OUTBOUND 메일의 SENT 이동을 막지 않아서, 수신자 상태행이 SENT 로 옮겨진 데이터가
     * 실제로 남아 있을 수 있다(그 행은 여전히 수신자 측이라 서버는 INBOX 이동을 허용한다).
     * 따라서 <b>INBOX 는 숨기지 않는다.</b>
     *
     * <p>확정적으로 판정 가능한 건 반대 방향뿐이다: direction==="INBOUND" 면 From 이 사내 직원이 아니므로
     * 발신자 측 상태행이 애초에 존재할 수 없고, 그 행은 무조건 수신자 측이다 → SENT 이동은 서버가 반드시
     * 거부하므로 메뉴에서 뺀다("죽은 항목" 제거).
     *
     * <p>나머지는 서버가 최종 판정하고 실패 사유는 토스트로 노출된다. 헛클릭 한 번보다 정상 이동 경로가
     * 통째로 사라지는 쪽이 훨씬 나쁜 회귀라, 애매하면 <b>숨기지 않는</b> 쪽을 택한다.
     * (direction 이 없는 목록 응답 — 구버전 캐시 등 — 도 같은 이유로 아무것도 숨기지 않는다.)
     */
    const blockedMoveTargets = useMemo(() => {
        if (selectedIds.length === 0) return [];
        const picked = new Set(selectedIds);
        const anyInbound = visibleMails.some(m => picked.has(m.id) && m.direction === "INBOUND");
        return anyInbound ? ["SENT"] : [];
    }, [selectedIds, visibleMails]);
    /**
     * 맨 위 전체 토글 — <b>하나라도 선택돼 있으면 비운다.</b> 아무것도 없을 때만 전체 선택.
     *
     * <p>예전엔 {@code allChecked} 만 봐서, 일부만 선택된 상태(체크박스에 '–' 가 떠 있는 상태)에서
     * 누르면 먼저 <b>전체 선택</b>이 되고 한 번 더 눌러야 비워졌다 — 해제하려고 눌렀는데 오히려
     * 선택이 늘어난다. 쉬프트 범위 선택이 생기면서 '일부 선택' 이 기본 상태에 가까워져 더 자주 걸린다.
     *
     * <p>'–' 는 "일부 선택됨" 이지 "전체 선택 직전" 이 아니다. 그 상태에서 사용자가 기대하는 건
     * 초기화 하나뿐이므로, 지우는 쪽으로 붙인다(지메일·아웃룩도 같다).
     */
    const handleToggleAll = () => {
        anchorRef.current = null;   // 전체 선택/해제는 범위의 기준점이 될 수 없다
        if (selectedIds.length > 0) setSelectedIds([]);
        else setSelectedIds(visibleMails.map(m => m.id));
    };

    /**
     * 체크박스 클릭 — 쉬프트를 누른 채면 <b>기준점부터 이번 행까지</b>를 한 번에 켜거나 끈다.
     *
     * <p><b>범위는 반드시 orderedMails(화면에 그려진 순서)로 잡는다.</b> 원본 mails 로 잡으면
     * 툴바에서 '오래된순' 을 켠 상태에서 위→아래로 끈 범위가 실제로는 뒤집혀 선택된다.
     *
     * <p>대상은 <b>현재 페이지 안</b>이다 — 서버 페이지네이션이라 다음 페이지 메일은 애초에
     * 화면(과 메모리)에 없다. 페이지 전체를 잡으려면 툴바/헤더의 전체 선택 체크박스를 쓴다.
     *
     * <p>기준점이 없거나(첫 클릭) 목록이 갈려 사라졌으면 평범한 단일 토글로 떨어진다 — 예외 없이
     * 무언가는 선택되게 해서, 쉬프트를 눌렀는데 아무 일도 안 일어나는 상황을 만들지 않는다.
     */
    const handleCheck = (id, on, shift) => {
        const anchor = anchorRef.current;
        anchorRef.current = id;

        // 켤 때 Set 을 거치는 이유: selectedIds 에 같은 id 가 두 번 들어가면 length 로 판정하는
        // allChecked(=== visibleMails.length) 가 어긋나, 전부 선택하지 않았는데 헤더 체크박스가
        // '전체 선택됨' 으로 켜진다. 범위 선택 쪽과 규칙을 맞춰 둔다.
        const single = () => setSelectedIds(prev => on ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
        if (!shift || anchor == null || anchor === id) return single();

        const from = orderedMails.findIndex(m => m.id === anchor);
        const to   = orderedMails.findIndex(m => m.id === id);
        if (from < 0 || to < 0) return single();

        const range = orderedMails
            .slice(Math.min(from, to), Math.max(from, to) + 1)
            .map(m => m.id);
        // 켤 때는 기존 선택에 합치고(Set 으로 중복 제거), 끌 때는 범위만 빼낸다 —
        // 범위 밖에서 따로 골라둔 선택은 건드리지 않는다.
        setSelectedIds(prev => on
            ? [...new Set([...prev, ...range])]
            : prev.filter(x => !range.includes(x)));
    };

    /* 일괄 액션 */
    /**
     * 일괄 API 결과 토스트 — 서버가 준 성공/실패 건수를 <b>그대로</b> 보여준다.
     *
     * <p>백엔드 일괄 API 는 건별 독립 트랜잭션이라 부분 성공이 정상 케이스다
     * ({@code {moved|restored: n, failed:[{mailId, reason}]}}).
     * 예전엔 응답을 무시하고 선택 건수를 출력해, 3건이 거부돼도 "10건 삭제" 라고 거짓말을 했다.
     * 실패가 있으면 첫 번째 reason 까지 함께 보여줘 왜 안 됐는지 알 수 있게 한다.
     *
     * @param res       일괄 API 응답 (구형 숫자 응답·Promise.all 배열도 허용)
     * @param requested 요청한 건수 — 서버가 성공 카운트를 안 줄 때의 폴백 계산용
     * @param label     "삭제" / "스팸 등록" 처럼 동사 라벨
     */
    const notifyBulkResult = (res, requested, label) => {
        const data = res?.data ?? res ?? {};
        // 구형 엔드포인트(batchDeleteForever 등)는 성공 건수를 숫자 하나로만 준다.
        if (typeof data === "number") { notify(`${data}건 ${label}`, "success"); return; }

        const failed  = Array.isArray(data.failed) ? data.failed : [];
        // moved(이동) / restored(복원) / deleted(영구삭제) 중 응답에 담긴 성공 카운트를 쓰고,
        // 카운트를 주지 않는 응답(Promise.all 묶음 등)만 요청 건수 - 실패 건수로 보정한다.
        const okCount = [data.moved, data.restored, data.deleted]
            .find(v => typeof v === "number") ?? (requested - failed.length);

        if (failed.length === 0) {
            notify(`${okCount}건 ${label}`, "success");
        } else {
            notify(`${okCount}건 ${label}, ${failed.length}건 실패 — ${failed[0]?.reason || "사유를 알 수 없습니다"}`,
                   okCount > 0 ? "warning" : "error");
        }
    };

    const bulk = async (fn, label) => {
        if (selectedIds.length === 0) return;
        const requested = selectedIds.length;
        try {
            const res = await fn();
            notifyBulkResult(res, requested, label);
            setSelectedIds([]);
            await loadList();
            refreshCounts();
        } catch (e) {
            // http 인터셉터가 서버 메시지를 Error.message 로 옮겨준다(response 객체는 남지 않는다).
            notify(e?.message || `${label} 실패`, "error");
        }
    };
    const handleBulkDelete  = () => bulk(() => mailApi.batchMove(selectedIds, "TRASH"),   "삭제");
    const handleBulkSpam    = () => bulk(() => mailApi.batchMove(selectedIds, "SPAM"),    "스팸 등록");
    // 스팸 해제·휴지통 복원은 둘 다 "원위치 복원" 이라 batchRestore 하나로 통일한다.
    // INBOX 로 하드코딩해 옮기면 원래 커스텀 폴더에 있던 메일이 받은 메일함으로 흘러가 버린다.
    const handleBulkUnspam  = () => bulk(() => mailApi.batchRestore(selectedIds), "스팸 해제");
    const handleBulkRestore = () => bulk(() => mailApi.batchRestore(selectedIds), "복원");

    /**
     * 스팸함 비우기 — <b>현재 목록에 보이는</b> 스팸 메일을 휴지통으로 옮긴다.
     *
     * <p>영구 삭제가 아니다. 스팸함에는 자동 삭제(보존 기간) 정책이 없으므로,
     * 쌓인 스팸을 치우려면 사용자가 직접 휴지통으로 보내야 한다. 휴지통에서 복원도 가능하다.
     * 페이지네이션 때문에 대상은 현재 페이지에 로드된 메일뿐이라 그 사실을 확인 문구에 밝힌다.
     */
    const handleEmptySpam = async () => {
        const ids = visibleMails.map(m => m.id);
        if (ids.length === 0) { notify("스팸함이 비어 있습니다", "info"); return; }
        const ok = await confirm({
            title: "스팸함 비우기",
            message: `현재 목록에 있는 ${ids.length}건을 휴지통으로 옮길까요?\n영구 삭제가 아니며, 휴지통에서 다시 복원할 수 있습니다.`,
            confirmLabel: "휴지통으로 이동",
        });
        if (!ok) return;
        try {
            const res = await mailApi.batchMove(ids, "TRASH");
            notifyBulkResult(res, ids.length, "휴지통으로 이동");
            setSelectedIds([]);
            await loadList();
            refreshCounts();
        } catch (e) {
            notify(e?.message || "스팸함 비우기 실패", "error");
        }
    };
    const handleBulkDeleteForever = async () => {
        if (selectedIds.length === 0) return;
        const ok = await confirm({
            title: "영구 삭제",
            message: `선택한 ${selectedIds.length}건을 영구 삭제할까요?\n내 메일함에서 완전히 사라지며 되돌릴 수 없습니다.`,
            variant: "danger",
            confirmLabel: "영구 삭제",
        });
        if (!ok) return;
        bulk(() => mailApi.batchDeleteForever(selectedIds), "영구 삭제");
    };
    const handleBulkMove    = (target) => bulk(() => mailApi.batchMove(selectedIds, target), "이동");
    const handleBulkMoveToFolder = (folderId) =>
        bulk(() => mailApi.batchMoveToFolder(selectedIds, folderId), "이동");
    const handleBulkMarkRead   = () => bulk(() => Promise.all(selectedIds.map(id => mailApi.markRead(id))),   "읽음 처리");
    const handleBulkMarkUnread = () => bulk(() => Promise.all(selectedIds.map(id => mailApi.markUnread(id))), "안읽음 처리");

    /* 상세 모달 액션 */
    /**
     * 단건 폴더 이동 — 실패 시 <b>서버가 준 사유를 그대로</b> 보여준다.
     *
     * <p>서버의 이동 가드(MailQueryService.move)는 "예약 발송 대기 중인 메일은 이동할 수 없습니다",
     * "임시보관함·예약함으로는 메일을 이동할 수 없습니다", "보낸 메일은 받은메일함으로 이동할 수 없습니다"
     * 처럼 <b>무엇을 어떻게 해야 하는지</b> 알려주는 409 메시지를 던진다. 예전엔 여기서 인자 없는
     * {@code catch} 로 삼키고 "이동 실패" 만 띄워, 사용자가 원인도 대안도 알 수 없었다
     * (일괄 이동은 failed[].reason 을 노출하는데 단건만 안 하는 비대칭이기도 했다).
     * http 인터셉터가 서버 message 를 Error.message 로 옮겨주므로 그대로 쓴다.
     */
    const handleMoveOne = async (mailId, targetFolder) => {
        try {
            await mailApi.move(mailId, targetFolder);
            setDetailOpen(false); setDetail(null);
            notify(`${FOLDERS.find(f => f.key === targetFolder)?.label}으로 이동했습니다`, "success");
            loadList(); refreshCounts();
        } catch (e) { notify(e?.message || "이동 실패", "error"); }
    };

    /**
     * 상세 모달의 스팸 해제 / 휴지통 복원 — 원위치 복원(서버가 previousFolder 처리, 없으면 INBOX).
     * MailDetail 도 자체 폴백을 갖고 있지만, 목록·사이드바 뱃지 갱신은 목록을 소유한 여기서 해야 한다.
     */
    const handleRestoreOne = async (mailId) => {
        try {
            await mailApi.restore(mailId);
            setDetailOpen(false); setDetail(null);
            notify("원래 폴더로 복원했습니다", "success");
            loadList(); refreshCounts();
        } catch (e) { notify(e?.message || "복원 실패", "error"); }
    };

    /**
     * 단건 커스텀 폴더 이동 — 실패 사유를 그대로 노출한다.
     * <p>커스텀 폴더 이동도 이제 {@code assertMovableByState} 를 공유하므로(예약 대기·초안 거부)
     * 거부될 수 있다. handleMoveOne 과 같은 이유로 서버 메시지를 그대로 쓴다.
     */
    const handleMoveOneToFolder = async (mailId, folderId) => {
        try {
            await mailApi.moveToFolder(mailId, folderId);
            setDetailOpen(false); setDetail(null);
            const name = customFolders.find(c => c.id === folderId)?.name || "폴더";
            notify(`${name}(으)로 이동했습니다`, "success");
            loadList(); refreshCounts();
        } catch (e) { notify(e?.message || "이동 실패", "error"); }
    };

    const handleCancelScheduled = async (mailId) => {
        const ok = await confirm({
            title: "예약 취소",
            // "다시 편집하거나" 라고 안내했었는데 편집 진입로가 없다 — 실제로 되는 것만 적는다.
            message: "이 메일의 예약 발송을 취소할까요?\n임시 보관함으로 이동되어 다시 발송하거나 삭제할 수 있습니다.",
            confirmLabel: "예약 취소",
        });
        if (!ok) return;
        try {
            await mailApi.cancelScheduled(mailId);
            setDetailOpen(false); setDetail(null);
            notify("예약을 취소했습니다 (임시 보관함으로 이동)", "success");
            loadList(); refreshCounts();
        } catch (e) { notify(e?.message || "예약 취소 실패", "error"); }
    };

    /**
     * 임시보관함 메일을 <b>저장된 그대로</b> 발송.
     *
     * <h4>편집과의 역할 분담</h4>
     * <p>편집({@link #handleEditDraft})은 <b>{@code editable=true} 인 초안</b>에만 열린다 —
     * 첨부가 없고 이관분도 아닌, 임시저장 버튼이 만든 초안. 그 외(이관분·발송 실패분)에는
     * 이 '그대로 발송' 이 유일한 수단이다.
     *
     * <h4>확인을 받는 이유</h4>
     * <p>여기 있는 메일은 <b>몇 년 전 이관분</b>일 수 있다. 실제로 외부로 나가고 되돌릴 수 없으므로
     * 제목·받는이를 다시 보여주고 확인을 받는다. 수신자가 없는 초안(이관분에 다수)은 서버가
     * "받는이가 비어있습니다" 로 400 을 준다 — 그건 편집이 필요한 건이라 문구로 안내만 한다.
     */
    const handleSendDraft = async (mailId) => {
        const target = detail && detail.id === mailId ? detail : null;
        // 상세 DTO 는 수신자를 recipients[{type,address}] 로 준다 — TO 만 뽑아 보여준다.
        const tos = (target?.recipients || []).filter(r => r.type === "TO").map(r => r.address);

        // 받는이 없는 초안 — 그냥 보내면 서버가 "받는이가 비어있습니다" 로 400 을 준다.
        // 그대로 노출하면 사용자는 '왜 안 되는지' 는 알아도 '그럼 어떻게 하는지' 를 모른다.
        // 편집이 열리는 초안이면 편집으로, 아니면(이관분 등) 전달로 안내한다.
        // (NAS 이관분에 받는이 없이 넘어온 초안이 다수 섞여 있어 실제로 자주 밟게 된다.)
        if (tos.length === 0) {
            notify(target?.editable
                ? "받는이가 없습니다. '편집'으로 받는이를 넣고 보내주세요."
                : "받는이가 없어 그대로 보낼 수 없습니다. '전달'로 받는이를 지정해 보내주세요.", "warning");
            return;
        }

        const ok = await confirm({
            title: "저장된 내용 그대로 발송",
            message: `이 메일을 지금 발송합니다. 내용은 저장된 그대로 나가며 되돌릴 수 없습니다.\n\n`
                   + `제목: ${target?.subject || "(제목 없음)"}\n`
                   + `받는이: ${tos.join(", ")}`,
            confirmLabel: "발송",
        });
        if (!ok) return;
        try {
            await mailApi.send(mailId);
            setDetailOpen(false); setDetail(null);
            notify("메일을 발송했습니다", "success");
            loadList(); refreshCounts();
        } catch (e) {
            notify(e?.message || "발송에 실패했습니다", "error");
        }
    };

    /**
     * 임시보관함 초안 편집 — 저장된 내용을 작성 창에 되돌린다.
     *
     * <p><b>{@code editable=true} 인 초안만</b> 여기 닿는다(버튼 자체가 그때만 나온다).
     * 서버가 판정하는 조건은 {@code MailDetailDto#editable} 주석에 있다 — 요약하면
     * 첨부 없는 순수 초안이어야 하고, NAS 이관분은 제외된다(본문이 Tiptap 을 거치며 손상된다).
     *
     * <p>첨부를 싣지 않는 이유도 같은 곳에 있다 — 임시저장이 애초에 첨부를 담지 않으므로
     * 편집으로 열 초안에는 첨부가 없다. 그래서 발송 시 {@code persistAttachments} 가
     * 기존 첨부 위에 덧붙는 '두 벌' 경로가 성립하지 않는다.
     */
    const handleEditDraft = async (mailId) => {
        try {
            const res  = await mailApi.getDetail(mailId);
            const mail = res?.data ?? res;
            if (!mail?.editable) {
                // 목록을 열어 둔 사이에 상태가 바뀌었을 수 있다(발송 실패로 첨부가 붙는 등).
                notify("이 메일은 편집할 수 없습니다. '발송'으로 저장된 내용을 그대로 보낼 수 있습니다.", "warning");
                return;
            }
            const tos  = (mail.recipients || []).filter(r => r.type === "TO").map(r => r.address);
            const ccs  = (mail.recipients || []).filter(r => r.type === "CC").map(r => r.address);
            const bccs = (mail.recipients || []).filter(r => r.type === "BCC").map(r => r.address);

            setDetailOpen(false); setDetail(null);
            setComposeInitial({
                draftId:  mail.id,          // ← 이 값이 있으면 MailWriteModal 이 '초안 편집' 모드로 연다
                tos, ccs, bccs,
                subject:  mail.subject || "",
                bodyHtml: mail.bodyHtml || "",
                inReplyTo:    mail.inReplyTo || null,
                references:   mail.referencesHeader || null,
                parentMailId: null,
            });
            setComposeOpen(true);
        } catch (e) {
            notify(e?.message || "초안을 불러오지 못했습니다", "error");
        }
    };

    /* 사용자 폴더 CRUD */
    const openCreateFolder = (parentId = null) => { setFolderNameInput(""); setFolderDialog({ mode: "create", parentId: parentId ?? null }); };
    const openRenameFolder = (f) => { setFolderNameInput(f.name); setFolderDialog({ mode: "rename", id: f.id }); };
    const closeFolderDialog = () => { if (!folderSaving) setFolderDialog(null); };
    const submitFolderDialog = async () => {
        const name = folderNameInput.trim();
        if (!name) { notify("폴더 이름을 입력해주세요", "warning"); return; }
        setFolderSaving(true);
        try {
            if (folderDialog?.mode === "create") {
                await mailApi.createFolder({ name, parentId: folderDialog.parentId ?? null });
                notify(folderDialog.parentId ? "하위 폴더를 만들었습니다" : "폴더를 만들었습니다", "success");
            } else {
                await mailApi.updateFolder(folderDialog.id, { name });
                notify("폴더 이름을 변경했습니다", "success");
            }
            setFolderDialog(null);
            await loadCustomFolders();
            loadCustomCounts();
        } catch (e) {
            notify(e?.message || "저장에 실패했습니다", "error");
        } finally {
            setFolderSaving(false);
        }
    };
    /**
     * 폴더 색상 변경 — 메뉴에서 색을 고르는 즉시 반영한다.
     *
     * <p>서버 응답을 기다렸다가 목록을 다시 불러오면 색이 늦게 바뀌어 "눌렀는데 안 되네" 로 읽힌다.
     * 그래서 화면을 먼저 바꾸고(낙관적 갱신) 실패하면 되돌린다. 색은 되돌려도 잃는 게 없는 값이라
     * 이 방식이 안전하다(같은 이유로 삭제·이름변경엔 쓰지 않는다).
     */
    const handleChangeFolderColor = async (f, color) => {
        if (!color || color === f.color) return;
        const prev = customFolders;
        setCustomFolders(list => (list || []).map(x => x.id === f.id ? { ...x, color } : x));
        try {
            await mailApi.updateFolder(f.id, { color });
        } catch (e) {
            setCustomFolders(prev);   // 롤백 — 서버가 거절했으면 화면도 원래 색으로
            notify(e?.message || "색상 변경에 실패했습니다", "error");
        }
    };

    /**
     * 폴더 이동(드래그&드롭) — 상위 변경 / 최상위 분리 / 형제 사이 순서 변경.
     *
     * <p>낙관적 갱신을 쓰지 않는다. 순환·깊이 초과는 서버만 최종 판정할 수 있고, 트리 구조를
     * 먼저 바꿨다가 되돌리면 접힘 상태·선택 폴더까지 흔들려 "뭐가 어디로 갔는지" 알 수 없게 된다.
     * (색상 변경과 달리 되돌려도 손해가 없는 값이 아니다.)
     */
    const handleMoveFolder = useCallback(async (id, { parentId = null, sortOrder = null } = {}) => {
        try {
            await mailApi.moveFolder(id, { parentId, sortOrder });
            await loadCustomFolders();
        } catch (e) {
            notify(e?.message || "폴더를 옮기지 못했습니다", "error");
            await loadCustomFolders();   // 서버가 거절했으면 화면을 서버 상태로 되돌린다
        }
    }, [loadCustomFolders]);

    const handleDeleteFolder = async (f) => {
        const ok = await confirm({
            title: "폴더 삭제",
            message: `'${f.name}' 폴더를 삭제할까요?\n폴더 안의 메일은 삭제되지 않고 받은 메일함으로 이동합니다.`,
            variant: "danger",
            confirmLabel: "삭제",
        });
        if (!ok) return;
        try {
            await mailApi.deleteFolder(f.id);
            notify("폴더를 삭제했습니다", "success");
            if (folder === customKey(f.id)) { setFolder("INBOX"); setPage(0); }
            else { loadList(); }
            await loadCustomFolders();
            refreshCounts();
        } catch (e) {
            notify(e?.message || "삭제에 실패했습니다", "error");
        }
    };

    const handleDeleteForeverOne = async (mailId) => {
        const ok = await confirm({
            title: "영구 삭제",
            message: "이 메일을 영구 삭제할까요?\n내 메일함에서 완전히 사라지며 되돌릴 수 없습니다.",
            variant: "danger",
            confirmLabel: "영구 삭제",
        });
        if (!ok) return;
        try {
            await mailApi.deleteForever(mailId);
            setDetailOpen(false); setDetail(null);
            notify("영구 삭제했습니다", "success");
            loadList(); loadUnread();
        } catch (e) {
            notify(e?.message || "영구 삭제 실패", "error");
        }
    };

    /* 작성 */
    const handleCompose = (initial = null) => {
        setComposeInitial(initial);
        setComposeOpen(true);
    };
    const handleComposeSelf = () => {
        if (!me?.email) { notify("내 이메일 정보를 불러오지 못했습니다", "warning"); return; }
        const myAddr = me.email.includes("@") ? me.email : `${me.email}@gayasoft.in`;
        handleCompose({
            tos: [`${me.name || ""} <${myAddr}>`],
            subject: "",
        });
    };

    /** 메일 상세에서 보낸이/받는이 칩 클릭 → 그 주소를 받는이로 새 메일 작성 */
    const handleComposeTo = (addressString) => {
        if (!addressString) return;
        setDetailOpen(false);
        setDetail(null);
        handleCompose({ tos: [addressString], subject: "" });
    };

    const handleReply = async (mail, all = false) => {
        try {
            const res = await mailApi.prepareReply(mail.id, all);
            const composed = res?.data ?? res;
            // 답장 본문에도 동일하게 cid → URL rewrite 적용 — 인용 블록의 이미지 정상 표시.
            if (composed?.quoteHtmlRaw) {
                composed.quoteHtmlRaw = rewriteImageUrls(composed.quoteHtmlRaw, mail.id, mail.attachments);
            }
            if (composed?.originalQuote) {
                composed.originalQuote = rewriteImageUrls(composed.originalQuote, mail.id, mail.attachments);
            }
            handleCompose(composed);
        } catch {
            handleCompose({
                tos: [mail.fromAddr],
                subject: `Re: ${mail.subject || ""}`,
                originalQuote: mail.bodyText || "",
            });
        }
        setDetailOpen(false);
    };
    const handleForward = async (mail) => {
        try {
            const res = await mailApi.prepareForward(mail.id);
            const composed = res?.data ?? res;
            // 인용 본문의 cid: → 우리 inline endpoint 절대 URL + token 으로 rewrite.
            // 편집 폼 read-only 영역에서 이미지가 정상 표시되고, 발송 시 백엔드가 그 URL 을
            // 새 cid + multipart/related inline 첨부로 변환해 외부 수신자도 정상 표시.
            if (composed?.quoteHtmlRaw) {
                composed.quoteHtmlRaw = rewriteImageUrls(composed.quoteHtmlRaw, mail.id, mail.attachments);
            }
            if (composed?.originalQuote) {
                composed.originalQuote = rewriteImageUrls(composed.originalQuote, mail.id, mail.attachments);
            }
            handleCompose(composed);
        } catch {
            handleCompose({ subject: `Fwd: ${mail.subject || ""}`, originalQuote: mail.bodyText || "" });
        }
        setDetailOpen(false);
    };

    /**
     * 다시보내기 — 보낸 메일을 그대로 작성 창에 되살린다.
     *
     * <p>전달과 달리 실패 시 fallback 을 두지 않는다. 전달의 fallback(제목만 살리고 본문은 텍스트)은
     * "원본을 소개하는 새 메일" 이라 반쪽이어도 쓸모가 있지만, 다시보내기는 원본과 같아야 의미가
     * 있어서 반쪽짜리로 열면 사용자가 그걸 눈치 못 채고 손상된 메일을 보낸다. 차라리 안 열고 알린다.
     */
    const handleResend = async (mail) => {
        try {
            const res = await mailApi.prepareResend(mail.id);
            const composed = res?.data ?? res;
            // 본문의 cid: / 상대 경로를 토큰 붙은 절대 URL 로 — 작성 창 편집 영역에서 이미지가 보이고,
            // 발송 시 백엔드가 그 URL 을 다시 cid + inline 첨부로 되돌린다(전달과 같은 경로).
            if (composed?.quoteHtmlRaw) {
                composed.quoteHtmlRaw = rewriteImageUrls(composed.quoteHtmlRaw, mail.id, mail.attachments);
            }
            if (composed?.originalQuote) {
                composed.originalQuote = rewriteImageUrls(composed.originalQuote, mail.id, mail.attachments);
            }
            handleCompose(composed);
            setDetailOpen(false);
        } catch {
            notify("다시보내기 데이터를 불러오지 못했습니다", "error");
        }
    };

    /* ── 렌더 ───────────────────────────────────────── */
    // ⚠ view 에 따라 early return 으로 분기하면 MailWriteModal 이 contacts 탭에서는 unmount 됨.
    //   그 결과 connacts 탭에서 "메일 쓰기" 누르면 state 만 set 되고 모달이 화면에 안 그려지는 사고.
    //   해결: 단일 return 으로 통합하고 메인 컨텐츠만 view 에 따라 swap. 모달은 항상 마운트.
    // full-bleed: 상위 main 이 height 100vh(상단 pt 제거)이므로 100% 로 꽉 채운다
    return (
        <Box sx={{ display: "flex", height: "100%", bgcolor: "#fff" }}>
            <FolderSidebar
                view={view} active={folder}
                onSelectFolder={(f) => {
                    // 폴더 변경 즉시 시각 피드백 — 이전 메일 리스트를 즉시 비우고 로딩 표시.
                    // 안 그러면 useEffect 가 loadList 를 비동기로 트리거하기 전까지 잠깐 이전 폴더의
                    // 리스트가 보여 사용자가 "내가 잘못 클릭했나?" 혼동하거나, 큰 폴더 로딩 시 응답 없는 듯한 인상.
                    setView("mail");
                    setMails([]);
                    setSelectedIds([]);
                    setLoading(true);

                    // 같은 폴더 + 같은 page 0 클릭은 React 가 setState 변화를 무시 → useEffect 안 트리거
                    // 되어 loading 만 stuck. (사용자 보고: "동일한 탭 두번 누르면 자료 불러오고 있어요만 뜨고 안돼")
                    // → 같은 폴더이고 이미 page 0 이면 loadList 직접 호출 (refresh 동등 동작).
                    if (f === folder && page === 0) {
                        loadList();
                    } else {
                        setFolder(f);
                        setPage(0);
                    }
                }}
                // 사이드바가 어느 주소록을 열지까지 지정한다 — 연락처 화면에 들어가서 다시 탭을 고르게 하면
                // "공용 연락처" 를 눌렀는데 개인 탭이 뜨는, 라벨과 결과가 어긋나는 화면이 된다.
                onShowContacts={(s = "PERSONAL") => { setContactScope(s); setView("contacts"); }}
                contactScope={contactScope}
                onShowBlockList={() => setView("block-list")}
                onShowGroups={() => setView("groups")}
                onShowSignatures={() => setView("signatures")}
                onShowAiPrompt={() => setView("ai-prompt")}
                onShowAdminAll={() => setView("admin-all")}
                onShowAdminUnmatched={() => setView("admin-unmatched")}
                onShowAdminMailbox={() => setView("admin-mailbox")}
                onShowMigration={() => setView("migration")}
                onShowArchive={() => setView("mail-archive")}
                canArchive={canArchive}
                canViewOthersMail={canViewOthersMail}
                onShowAttachments={() => setView("attachments")}
                isAdmin={isAdmin}
                unreadCounts={unreadCounts}
                totalCounts={totalCounts}
                starredCount={starredCount}
                onCompose={() => handleCompose()}
                onComposeSelf={handleComposeSelf}
                customFolders={customFolders}
                customCounts={customCounts}
                onCreateFolder={openCreateFolder}
                onRenameFolder={openRenameFolder}
                onDeleteFolder={handleDeleteFolder}
                onChangeFolderColor={handleChangeFolderColor}
                onMoveFolder={handleMoveFolder}
            />

            {view === "contacts" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    {/* 연락처에서 바로 메일 작성 — MailWriteModal 은 MailPage 가 소유하므로 콜백으로 넘긴다.
                        (이 모달은 contacts 탭에서도 unmount 되지 않도록 아래쪽에 항상 렌더돼 있다) */}
                    <ContactsPanel onComposeMail={handleCompose} initialScope={contactScope} />
                </Box>
            ) : view === "groups" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    {/* 그룹으로 바로 메일 쓰기 — MailWriteModal 은 MailPage 가 소유하므로 콜백으로 넘긴다. */}
                    <ContactGroupPanel onComposeMail={handleCompose} />
                </Box>
            ) : view === "block-list" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    <BlockListPanel isAdmin={isAdmin} />
                </Box>
            ) : view === "signatures" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    <MySignaturesPanel />
                </Box>
            ) : view === "ai-prompt" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    <AdminPromptPanel />
                </Box>
            ) : view === "admin-all" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    {/* 관리자 전체 메일 — 행 클릭은 관리자 열람 경로(getMailAsAdmin, READ_OTHER 감사) */}
                    <AdminMailPanel mode="all" onOpenMail={handleOpenAsAdmin} />
                </Box>
            ) : view === "admin-unmatched" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    <AdminMailPanel mode="unmatched" onOpenMail={handleOpenAsAdmin} />
                </Box>
            ) : view === "admin-mailbox" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    {/* 특정 직원의 편지함 — 행 클릭은 관리자 열람 경로(getMailAsAdmin, READ_OTHER 감사) */}
                    <EmployeeMailboxPanel onOpenMail={handleOpenAsAdmin} />
                </Box>
            ) : view === "migration" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    <MailMigrationPage />
                </Box>
            ) : view === "mail-archive" ? (
                <Box sx={{ flex: 1, overflowY: "auto", bgcolor: "#fafbfc" }}>
                    <MailArchivePage />
                </Box>
            ) : view === "attachments" ? (
                <Box sx={{ flex: 1, overflow: "hidden", bgcolor: "#fafbfc" }}>
                    <AttachmentLibraryPanel
                        onOpenMail={(mailId) => handleOpen({ id: mailId })}
                    />
                </Box>
            ) : (
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                {/* 페이지 헤더 — 폴더명 + 카운트 + 검색 + 새로고침 (설명과 디바이더 X) */}
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5,
                    px: 2.5, py: 1.5, bgcolor: "#fff",
                    flexShrink: 0,
                }}>
                    {headerIcon}
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                        {folderMeta.label}
                    </Typography>

                    <Box sx={{ flex: 1 }} />

                    {/* 중요만 보기 — 진짜 폴더에서만 뜬다.
                        «중요»·«안 읽은 메일» 같은 가상 폴더에서는 뜻이 겹치거나(중요 안에서 중요만)
                        서버에 그 조합의 조회가 없다(안읽음 ∩ 별표). 검색 중에도 숨긴다. */}
                    {!searchActive && !folderMeta.virtual && !isCustomKey(folder) && (
                        <Tooltip title={starredOnly ? "전체 보기" : "중요만 보기"}>
                            <IconButton size="small"
                                onClick={() => { setStarredOnly(v => !v); setPage(0); }}
                                sx={{ color: starredOnly ? "#f59e0b" : "#94a3b8" }}>
                                {starredOnly
                                    ? <StarRoundedIcon       sx={{ fontSize: 18 }} />
                                    : <StarBorderRoundedIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* 새로고침 = 목록 + 카운트. 목록만 다시 받으면 새 메일이 줄에는 뜨는데
                        옆의 안 읽음 숫자는 그대로라 "새로고침이 안 먹는다" 로 보인다. */}
                    <Tooltip title="새로고침">
                        <IconButton size="small" onClick={() => { loadList(); refreshCounts(); }} disabled={loading}>
                            {/* 로딩 중엔 아이콘 회전 */}
                            <RefreshRoundedIcon sx={{
                                fontSize: 18,
                                animation: loading ? "spin 1s linear infinite" : "none",
                                "@keyframes spin": {
                                    "0%":   { transform: "rotate(0deg)" },
                                    "100%": { transform: "rotate(360deg)" },
                                },
                            }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* 폴더 설명 — 흰 배경 */}
                <Box sx={{
                    px: 2.5, py: 1, bgcolor: "#fff", borderBottom: "1px solid #eef2f6",
                    flexShrink: 0,
                }}>
                    <Typography sx={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
                        {folderMeta.desc}
                    </Typography>
                </Box>

                {/* 예약함 — '예약 메일 / 단체 발송' 탭 */}
                {folder === "SCHEDULED" && (
                    <Tabs
                        value={scheduledTab}
                        onChange={(e, v) => setScheduledTab(v)}
                        sx={{ minHeight: 40, px: 1.5, bgcolor: "#fff", borderBottom: "1px solid #eef2f6",
                              "& .MuiTab-root": { minHeight: 40, fontSize: 13, fontWeight: 700, textTransform: "none" } }}>
                        <Tab icon={<ScheduleSendRoundedIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="예약 메일" />
                        <Tab icon={<CampaignRoundedIcon    sx={{ fontSize: 17 }} />} iconPosition="start" label="단체 발송" />
                    </Tabs>
                )}

                {folder === "SCHEDULED" && scheduledTab === 1 ? (
                    <Box sx={{ flex: 1, overflow: "auto", bgcolor: "#fafbfc" }}>
                        <BulkMailPanel />
                    </Box>
                ) : folder === "SCHEDULED" && scheduledTab === 0 ? (
                    <Box sx={{ flex: 1, overflow: "auto", bgcolor: "#fafbfc" }}>
                        <ScheduledMailPanel onOpenMail={(id) => handleOpen({ id, isRead: true })} />
                    </Box>
                ) : (
                <>
                {/* 툴바 */}
                <MailToolbar
                    selectedIds={selectedIds}
                    allChecked={allChecked} indeterminate={indeterminate}
                    onToggleAll={handleToggleAll}
                    folder={folder}
                    searchMode={searchActive}
                    pageSize={pageSize} onPageSize={(n) => { setPageSize(n); setPage(0); }}
                    sortDesc={sortDesc} onToggleSort={() => setSortDesc(v => !v)}
                    onBulkDelete={handleBulkDelete}
                    onBulkSpam={handleBulkSpam}
                    onBulkUnspam={handleBulkUnspam}
                    onBulkRestore={handleBulkRestore}
                    onBulkDeleteForever={handleBulkDeleteForever}
                    onEmptySpam={handleEmptySpam}
                    onBulkMove={handleBulkMove}
                    onBulkMoveToFolder={handleBulkMoveToFolder}
                    blockedMoveTargets={blockedMoveTargets}
                    customFolders={customFolders}
                    onBulkMarkRead={handleBulkMarkRead}
                    onBulkMarkUnread={handleBulkMarkUnread}
                />

                {/* 검색 — 툴바(최신순/N개) 바로 아래 */}
                <Box sx={{
                    px: 2, py: 1, bgcolor: "#fff", borderBottom: "1px solid #f1f5f9",
                    flexShrink: 0,
                }}>
                    <TextField
                        size="small" fullWidth placeholder="전체 메일에서 보낸이·제목·미리보기 검색 (휴지통 제외)"
                        value={query} onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchRoundedIcon sx={{ fontSize: 16, color: "#94a3b8", mr: 0.5 }} />,
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { height: 32, fontSize: 13, bgcolor: "#fff" } }}
                    />
                </Box>

                {/* 컬럼 헤더 + 메일 목록 — 반드시 '같은' 스크롤 상자 안에 있어야 한다.
                    예전엔 헤더가 이 상자 밖이라 세로 고정만 됐고 가로 스크롤은 목록만 움직여
                    라벨과 값이 어긋났다. 이제 헤더는 안에서 position:sticky 로 세로만 고정한다.
                    막대는 세로만 감춘다(hiddenScrollbarY) — 가로 막대까지 지우면 오른쪽에 열이
                    더 있다는 사실이 화면에서 완전히 사라진다. */}
                <Box sx={{ flex: 1, overflow: "auto", bgcolor: "#fff", ...ds.hiddenScrollbarY }}>
                    {/* 헤더와 행이 함께 차지하는 최소 폭. 이게 없으면 둘 다 상자 폭(=100%)에 맞춰 그려져
                        컬럼이 넘쳐도 scrollWidth 가 늘지 않아 가로 스크롤 자체가 생기지 않는다. */}
                    <Box sx={{ minWidth: listMinWidthFor(folder) }}>
                        <MailListHeader
                            folder={folder}
                            allChecked={allChecked}
                            indeterminate={indeterminate}
                            onToggleAll={handleToggleAll}
                        />
                        {!loading && visibleMails.length > 0 && orderedMails.map(m => (
                            <MailRow
                                key={m.id} mail={m}
                                folder={folder}
                                folderNameById={folderNameById}
                                checked={selectedIds.includes(m.id)}
                                onCheck={handleCheck}
                                onOpen={() => handleOpen(m)}
                                onToggleStar={handleToggleStar}
                            />
                        ))}
                    </Box>

                    {/* 로딩·빈 상태는 최소 폭 래퍼 밖에 둔다 — 안에 넣으면 안내 문구가 표 폭(726px)을
                        기준으로 가운데 정렬돼 좁은 화면에선 오른쪽 화면 밖에 놓인다. */}
                    {loading ? (
                        <Box sx={{ p: 6 }}><LoadingDots label="목록을 불러오고 있어요"/></Box>
                    ) : visibleMails.length === 0 ? (
                        <Box sx={{ p: 8, textAlign: "center", color: "#94a3b8" }}>
                            <EmailRoundedIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.4 }} />
                            <Typography sx={{ fontSize: 13.5 }}>
                                {query ? "검색 결과가 없습니다." : "이 폴더에 메일이 없습니다."}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>

                {/* 페이지 이동 — 목록 아래 가운데. 공용 컴포넌트로 통일해 다른 표와 조작법을 맞춘다. */}
                <TablePaginator
                    totalPages={totalPages}
                    page={page + 1}
                    onPageChange={(p) => setPage(p - 1)}
                />

                </>
                )}
            </Box>
            )}

            {/* 상세 풀스크린 모달 — MailDetail 내부 툴바가 닫기 X 까지 처리하므로 별도 헤더 X */}
            <Dialog
                open={detailOpen} onClose={() => { setDetailOpen(false); setDetail(null); }}
                fullWidth
                PaperProps={{ sx: { maxWidth: 1500, height: "110vh", display: "flex", flexDirection: "column" } }}>
                <DialogContent sx={{ p: 0, flex: 1, overflow: "auto" }}>
                    {detailLoading ? (
                        <Box sx={{
                            display: "flex", flexDirection: "column",
                            alignItems: "center", justifyContent: "center",
                            height: "100%", gap: 2, color: "#94a3b8",
                        }}>
                            <LoadingDots label="메일을 불러오고 있어요"/>
                        </Box>
                    ) : detail && (
                        <MailDetail
                            mail={detail}
                            /* 검색 결과는 폴더 무관이라 사이드바 폴더를 그대로 넘기면 상세 툴바가
                               실제와 다른 폴더 기준으로 그려진다(스팸함에서 검색해 연 받은 메일에 '스팸 해제').
                               상세 DTO 가 본인 기준 folder 를 주므로 그 값을 우선한다. */
                            folder={detail?.folder || folder}
                            onClose={() => { setDetailOpen(false); setDetail(null); }}
                            onMove={handleMoveOne}
                            onMoveToFolder={handleMoveOneToFolder}
                            onCancelScheduled={handleCancelScheduled}
                            onSendDraft={handleSendDraft}
                            onEditDraft={handleEditDraft}
                            customFolders={customFolders}
                            onDeleteForever={handleDeleteForeverOne}
                            onReply={(all) => handleReply(detail, all)}
                            onForward={() => handleForward(detail)}
                            onResend={() => handleResend(detail)}
                            onToggleStar={() => handleToggleStar(detail.id)}
                            onComposeTo={handleComposeTo}
                            /* 상세에서 차단 규칙을 추가/해제하면 서버가 소급 이동까지 하므로
                               목록과 사이드바 뱃지를 다시 읽어야 한다. 안 그러면 이미 스팸함으로 옮겨간
                               메일이 받은 메일함에 그대로 남아 있는 것처럼 보인다. */
                            onRulesChanged={() => { loadList(); refreshCounts(); }}
                            onRestore={handleRestoreOne}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <MailWriteModal
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                initial={composeInitial}
                onSent={() => { setComposeOpen(false); loadList(); refreshCounts(); }}
            />

            {/* 사용자 폴더 생성/이름변경 다이얼로그 */}
            <Dialog open={Boolean(folderDialog)} onClose={closeFolderDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
                    {folderDialog?.mode === "rename" ? "폴더 이름 변경"
                        : folderDialog?.parentId ? "하위 폴더 만들기" : "새 폴더 만들기"}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus fullWidth size="small" label="폴더 이름"
                        value={folderNameInput}
                        onChange={(e) => setFolderNameInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") submitFolderDialog(); }}
                        inputProps={{ maxLength: 100 }}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeFolderDialog} disabled={folderSaving} sx={{ textTransform: "none", color: "#64748b" }}>
                        취소
                    </Button>
                    <Button onClick={submitFolderDialog} variant="contained" disabled={folderSaving}
                            sx={{ textTransform: "none", fontWeight: 700 }}>
                        {folderSaving ? "저장 중..." : (folderDialog?.mode === "rename" ? "변경" : "만들기")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
